import StockFormatReviewDialog from '../components/StockFormatReviewDialog';
import { formatPesoAmount } from '../utils/money';
import useCatalogUpdateQueue from '../hooks/useCatalogUpdateQueue';
import { fetchCatalogDelta, mergeCatalogDelta, shareCatalogValue } from '../utils/catalogDelta';
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { isStockPack, getStockPackError, stockPackLabel, stockPackTotal } from "../utils/stockPack";
import { useNavigate, useParams } from "react-router-dom";
import {
  addStockPayment,
  getStockList,
  getExpensesByStockList,
  uploadFile,
  deleteStock,
  getProducts,
  getProductPresentations,
  getProviders,
  getEstimateUnitEquivalences,
  upsertEstimateUnitEquivalence,
  deleteEstimateUnitEquivalence,
  getStockEstimateConfirmations,
  confirmStockEstimateItem,
  unconfirmStockEstimateItem,
  finalizeStockList,
  runStockListActivationNow,
  getStockListPriceRecommendationRuns,
  runStockListPriceRecommendations,
  updateStockListActivationSchedule,
  updateStock,
  getImportConversions,
} from "../api";
import { FiChevronLeft, FiChevronRight, FiEye, FiInfo } from "react-icons/fi";
import useCurrentUser from "../hooks/useCurrentUser";
import { hasPermission } from "../utils/permissions";
import { normalizePaginatedResponse } from "../utils/pagination";
import {
  calculateStockAmounts,
  resolveProviderTotalAmount,
  resolveStockCostAmount,
  resolveIvaCostUpliftAmount,
} from "../utils/stockCost";
import InlineLoading from "../components/InlineLoading";
import StockPurchaseFormatSelector from '../components/StockPurchaseFormatSelector';
import StockFormatRegistrationNotice from '../components/StockFormatRegistrationNotice';
import StockFormatRegistrationDialog from '../components/StockFormatRegistrationDialog';
import { getPurchaseFormatError, isFormatStockPurchase, stockPurchaseFields, stockRegistryDraft } from '../utils/stockPurchaseFormat';
import { mergeStockPurchaseCatalog } from '../utils/stockPurchaseCatalog';
import { buildWholesalePriceMap, resolveStockWholesaleEstimate, toMetricUnitKey } from '../utils/stockWholesaleEstimate';
import StockExpenseDialog from '../components/StockExpenseDialog';
import StockAccountingEmailAction from '../components/StockAccountingEmailAction';
import { notify } from '../utils/alertBus';
import useConfirmDialog from '../hooks/useConfirmDialog';
import useProductEvents from '../hooks/useProductEvents';
import usePurchaseEvents from '../hooks/usePurchaseEvents';
import {
  getStandardStockPurchaseGuidance,
  getStandardStockWeightError,
  isStandardGranelPurchase,
} from '../utils/stockEntryGuidance';

const getTodayDate = () => new Date().toISOString().split("T")[0];
const ESTIMATE_WARNING_THRESHOLD = 5;
const STOCK_METRIC_UNIT_OPTIONS = [
  { value: "pack", label: "Pack de X unidades" },
  { value: "gamela", label: "Gamela" },
  { value: "balde", label: "Balde" },
  { value: "granel", label: "Granel" },
  { value: "bin", label: "Bin" },
  { value: "malla", label: "Malla" },
  { value: "cajonMadera", label: "Cajón de madera" },
  { value: "caja", label: "Caja" },
  { value: "saco", label: "Saco" },
  { value: "cajonNegro", label: "Cajón Negro" },
  { value: "cajonBlanco", label: "Cajón Blanco" },
];
const STOCK_PAYMENT_METHODS = [
  { id: "efectivo", label: "Efectivo" },
  { id: "transferencia", label: "Transferencia" },
  { id: "tarjeta", label: "Tarjeta" },
  { id: "cheque", label: "Cheque" },
];
const CLIENT_TYPE_LABELS = {
  detalle: "Detalle",
  mayorista: "Mayorista",
};
const RECOMMENDATION_STATUS_LABELS = {
  applied: "Aplicado",
  recommended: "Recomendado",
  unchanged: "Sin cambio",
  skipped: "Omitido",
};
const RECOMMENDATION_STATUS_CLASSES = {
  applied: "bg-green-100 text-green-700",
  recommended: "bg-blue-100 text-blue-700",
  unchanged: "bg-gray-100 text-gray-600",
  skipped: "bg-amber-100 text-amber-700",
};
const RECOMMENDATION_RUN_STATUS_LABELS = {
  running: "En ejecucion",
  completed: "Completada",
  failed: "Fallida",
};
const RECOMMENDATION_RUN_STATUS_CLASSES = {
  running: "bg-indigo-100 text-indigo-700",
  completed: "bg-green-100 text-green-700",
  failed: "bg-red-100 text-red-700",
};
const RECOMMENDATION_REVIEW_STATUSES = new Set(["applied", "recommended"]);
const RECOMMENDATION_ITEMS_PAGE_SIZE = 10;
const AUTO_ACTIVATION_STATUS_LABELS = {
  scheduled: "Programada",
  running: "En ejecución",
  completed: "Completada",
  failed: "Fallida",
};
const AUTO_ACTIVATION_STATUS_CLASSES = {
  scheduled: "bg-blue-100 text-blue-700",
  running: "bg-indigo-100 text-indigo-700",
  completed: "bg-green-100 text-green-700",
  failed: "bg-red-100 text-red-700",
};

const isBinUnit = (value) => String(value || "").trim().toLowerCase() === "bin";
const isGranelUnit = (value) => String(value || "").trim().toLowerCase() === "granel";
const getStockBultos = (stock) => {
  const quantity = Number(stock?.quantity || 0);
  if (!Number.isFinite(quantity) || quantity <= 0) return 0;
  if (!isFormatStockPurchase(stock) && isBinUnit(stock?.metricUnit)) {
    const bultosPerBin = Number(stock?.bultosPerBin || 0);
    if (!Number.isFinite(bultosPerBin) || bultosPerBin <= 0) return 0;
    return quantity * bultosPerBin;
  }
  if (!isFormatStockPurchase(stock) && isGranelUnit(stock?.metricUnit)) {
    return 0;
  }
  return quantity;
};

export const getExplicitStandardGranelWeight = (stock, { isImportList = false } = {}) => {
  if (!isStandardGranelPurchase({ isImportList, metricUnit: stock?.metricUnit, purchaseMode: stock?.purchaseMode })) return null;
  const effectiveExplicitWeightKg = Number(stock?.costInputContext?.effectiveInputs?.weightKg);
  if (Number.isFinite(effectiveExplicitWeightKg) && effectiveExplicitWeightKg > 0) {
    return effectiveExplicitWeightKg;
  }
  const explicitWeightKg = Number(stock?.weightKg);
  if (Number.isFinite(explicitWeightKg) && explicitWeightKg > 0) return explicitWeightKg;
  const originalWeight = Number(stock?.costInputContext?.originalInputs?.weight);
  const effectiveWeight = Number(
    stock?.costInputContext?.effectiveInputs?.weight ?? stock?.weight
  );
  if (stock?.costInputContext?.latestEdit
    && Number.isFinite(effectiveWeight)
    && effectiveWeight > 0
    && (!Number.isFinite(originalWeight) || effectiveWeight !== originalWeight)) {
    return effectiveWeight;
  }
  return null;
};

export const getStandardGranelDisplayWeight = (stock, options = {}) => {
  const explicitWeight = getExplicitStandardGranelWeight(stock, options);
  if (explicitWeight !== null) return explicitWeight;
  const quantity = Number(stock?.quantity || 0);
  return Number.isFinite(quantity) && quantity > 0 ? quantity : 0;
};

export const getStockKg = (stock, { isImportList = false } = {}) => {
  const quantity = Number(stock?.quantity || 0);
  if (!Number.isFinite(quantity) || quantity <= 0) return 0;
  if (!isFormatStockPurchase(stock) && isBinUnit(stock?.metricUnit)) {
    if (isImportList) return quantity;
    const binWeight = Number(stock?.weight ?? 0);
    return Number.isFinite(binWeight) && binWeight > 0
      ? quantity * binWeight
      : quantity;
  }
  if (!isFormatStockPurchase(stock) && isGranelUnit(stock?.metricUnit)) {
    if (isImportList) return quantity;
    return getStandardGranelDisplayWeight(stock, { isImportList });
  }
  const weight = Number(stock?.purchaseSnapshot?.weightKg ?? stock?.weightKg ?? stock?.weight ?? 0);
  if (!Number.isFinite(weight) || weight <= 0) return 0;
  return quantity * weight;
};

const formatKg = (value) =>
  Number(value || 0).toLocaleString("de-DE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatSignificantNumber = (value, maxFractionDigits = 6) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "0";
  return numeric.toLocaleString("de-DE", {
    minimumFractionDigits: 0,
    maximumFractionDigits: maxFractionDigits,
  });
};

const sellingFormatLabel = (meta) => meta?.wholesaleMetricUnit
  ? `${meta.wholesaleMetricUnit}${meta.sellingWeightKg ? ` · ${formatSignificantNumber(meta.sellingWeightKg)} kg` : ""}`
  : "Pendiente";

const StockSellingEstimateSummary = ({ meta, loading }) => (
  <div className="text-xs text-emerald-800">
    <p><span className="font-semibold">Formato de venta (estimado):</span>{" "}
      {loading ? "Cargando..." : sellingFormatLabel(meta)}
    </p>
    {!loading && (meta?.considered ? (
      <p>{formatSignificantNumber(meta.quantity)} {meta.wholesaleMetricUnit} × ${formatPesoAmount(Number(meta.unitPrice))} = ${formatPesoAmount(Number(meta.estimate))}</p>
    ) : (
      <p className="text-amber-800">Sin estimado: {meta?.reason || "Faltan datos del formato de venta."}</p>
    ))}
    {!loading && meta?.considered && meta.priceSource === 'draft' && (
      <p>Precio provisional según el costo de esta compra y su regla de precio.</p>
    )}
  </div>
);

const parseDecimalInput = (value) => {
  const normalized = String(value ?? "").trim().replace(",", ".");
  if (!normalized) return null;
  const numeric = Number(normalized);
  return Number.isFinite(numeric) ? numeric : null;
};

const formatDateTimeLocal = (value) => {
  const date = value instanceof Date ? value : new Date(value);
  if (!Number.isFinite(date.getTime())) return "";
  const pad = (entry) => String(entry).padStart(2, "0");
  return [
    date.getFullYear(),
    pad(date.getMonth() + 1),
    pad(date.getDate()),
  ].join("-") + `T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const DEFAULT_ACTIVATION_HOURS = 18;
const getDefaultActivationInput = () =>
  formatDateTimeLocal(new Date(Date.now() + DEFAULT_ACTIVATION_HOURS * 60 * 60 * 1000));

const formatDateTimeDisplay = (value) => {
  if (!value) return "-";
  const date = value instanceof Date ? value : new Date(value);
  return Number.isFinite(date.getTime()) ? date.toLocaleString("es-CL") : "-";
};

const getRecommendationRunModeLabel = (run) => (
  run?.autoUpdatePrices === false ? "Solo recomendaciones" : "Auto-actualizacion"
);

const getRecommendationRunLog = (run) => (
  Array.isArray(run?.summary?.processLog) ? run.summary.processLog : []
);

const getRecommendationItemImage = (item) =>
  item?.VarietyPrice?.priceImage ||
  item?.Variety?.varietyImage ||
  item?.Variety?.Product?.productImage ||
  "https://placehold.co/96";

const getRecommendationProductName = (item) =>
  item?.Variety?.Product?.productName || `Producto ${item?.Variety?.productId || "-"}`;

const getRecommendationVarietyName = (item) =>
  item?.Variety?.varietyName || `Variedad ${item?.varietyId || "-"}`;

const getRecommendationVarietyPriceLabel = (item) => {
  const price = item?.VarietyPrice || {};
  const formatName = price.ProductFormat?.name || CLIENT_TYPE_LABELS[item?.clientType] || item?.clientType || "";
  const unit = price.metricUnit || "";
  const description = price.description || "";
  const label = [description, unit, formatName].filter(Boolean).join(" · ");
  return label || `Precio ${item?.varietyPriceId || "-"}`;
};

const formatRecommendationMoney = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "-";
  return `$${formatPesoAmount(numeric)}`;
};

const parseFiniteNumber = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

const recommendationAmountsMatch = (left, right) => {
  const leftNumber = parseFiniteNumber(left);
  const rightNumber = parseFiniteNumber(right);
  if (leftNumber === null || rightNumber === null) return false;
  return Math.abs(leftNumber - rightNumber) < 0.01;
};

const getStoredReferenceCostText = (breakdown, expectedValue = null, expectedFormula = null) => {
  const stored = breakdown?.referenceCostRaw ?? breakdown?.referenceCost;
  const expected = parseFiniteNumber(expectedValue);
  const expectedText = expected !== null
    ? ` (${expectedFormula || "fórmula actual"} daría ${formatRecommendationMoney(expected)})`
    : "";
  return `Costo histórico guardado: ${formatRecommendationMoney(stored)}${expectedText}`;
};

const parseRecommendationRuleNumber = (value) => {
  const normalized = String(value || "").replace(/\./g, "").replace(",", ".");
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

const parseRecommendationRuleFromLabel = (label) => {
  const text = String(label || "").trim();
  const amountMatch = text.match(/^Sumar \$([\d.,]+)(\/kg)?$/);
  if (amountMatch) {
    const value = parseRecommendationRuleNumber(amountMatch[1]);
    return value === null ? null : { mode: "markup_amount", value, basis: amountMatch[2] ? "kg" : "unit" };
  }
  const percentMatch = text.match(/^Sumar ([\d.,]+)%$/);
  if (percentMatch) {
    const value = parseRecommendationRuleNumber(percentMatch[1]);
    return value === null ? null : { mode: "markup_percent", value };
  }
  const fixedMatch = text.match(/^Precio fijo \$([\d.,]+)(\/kg)?$/);
  if (fixedMatch) {
    const value = parseRecommendationRuleNumber(fixedMatch[1]);
    return value === null ? null : { mode: "fixed_price", value, basis: fixedMatch[2] ? "kg" : "unit" };
  }
  return null;
};

const getRecommendationCalculationSnapshot = (item) => (
  item?.calculationSnapshot && typeof item.calculationSnapshot === "object"
    ? item.calculationSnapshot
    : {}
);

const getRecommendationRule = (item) => {
  const snapshot = getRecommendationCalculationSnapshot(item);
  return snapshot.rule || parseRecommendationRuleFromLabel(snapshot.ruleName || item?.ruleName);
};

const getRecommendationRuleLabel = (item) => {
  const snapshot = getRecommendationCalculationSnapshot(item);
  return snapshot.ruleName || item?.ruleName || item?.skipReason || "-";
};

const getRecommendationCalculationExpression = (item) => {
  const snapshot = getRecommendationCalculationSnapshot(item);
  const rule = getRecommendationRule(item);
  const isKgBasis = snapshot.pricingBasis === "kg" || rule?.basis === "kg";
  const referenceCost = isKgBasis
    ? snapshot.referenceCostPerKgRaw ?? snapshot.referenceCostRaw ?? item?.referenceCostRaw ?? item?.referenceCost
    : snapshot.referenceCostRaw ?? item?.referenceCostRaw ?? item?.referenceCost;
  const rawPrice = isKgBasis
    ? snapshot.rawPricePerKg ?? snapshot.rawPrice ?? item?.recommendedPriceRaw
    : snapshot.rawPrice ?? item?.recommendedPriceRaw;
  if (!rule || rawPrice === null || rawPrice === undefined) return null;
  if (rule.mode === "markup_amount") {
    return `${formatRecommendationMoney(referenceCost)} + ${formatRecommendationMoney(rule.value)} = ${formatRecommendationMoney(rawPrice)}`;
  }
  if (rule.mode === "markup_percent") {
    return `${formatRecommendationMoney(referenceCost)} x (1 + ${formatSignificantNumber(rule.value, 4)} / 100) = ${formatRecommendationMoney(rawPrice)}`;
  }
  if (rule.mode === "fixed_price") {
    return `${formatRecommendationMoney(rule.value)} = ${formatRecommendationMoney(rawPrice)}`;
  }
  return null;
};

const getRecommendationRoundingMeta = (rounding = {}) => {
  const unit = Number(rounding.unit) === 10 || rounding.mode === "tens_threshold" ? 10 : 100;
  return {
    label: unit === 10 ? "Decenas" : "Centenas",
    threshold: rounding.threshold ?? (unit === 10 ? 2 : 20),
  };
};

const getRecommendationCalculationDetails = (item, run) => {
  const snapshot = getRecommendationCalculationSnapshot(item);
  const rule = getRecommendationRule(item);
  const isKgBasis = snapshot.pricingBasis === "kg" || rule?.basis === "kg";
  const referenceCost = isKgBasis
    ? snapshot.referenceCostPerKgRaw ?? snapshot.referenceCostRaw ?? item?.referenceCostRaw ?? item?.referenceCost
    : snapshot.referenceCostRaw ?? item?.referenceCostRaw ?? item?.referenceCost;
  const rawPrice = isKgBasis
    ? snapshot.rawPricePerKg ?? snapshot.rawPrice ?? item?.recommendedPriceRaw
    : snapshot.rawPrice ?? item?.recommendedPriceRaw;
  const finalPrice = snapshot.roundedPrice ?? item?.recommendedPrice;
  const finalPricePerKg = isKgBasis ? snapshot.roundedPricePerKg : null;
  const oldPrice = snapshot.oldPrice ?? item?.oldPrice;
  const rounding = snapshot.rounding || run?.configSnapshot?.rounding || {};
  const expression = getRecommendationCalculationExpression(item);
  const details = [
    { label: isKgBasis ? "Costo referencia / kg" : "Costo referencia", value: formatRecommendationMoney(referenceCost) },
    { label: "Regla", value: getRecommendationRuleLabel(item) },
  ];

  if (expression) {
    details.push({ label: "Calculo", value: expression });
  }

  details.push({
    label: "Resultado sin redondeo",
    value: formatRecommendationMoney(rawPrice),
  });

  if (rule?.mode === "fixed_price" || rounding.applied === false) {
    details.push({ label: "Redondeo", value: "No aplica para precio fijo." });
  } else {
    const roundingMeta = getRecommendationRoundingMeta(rounding);
    const roundedBasisPrice = isKgBasis ? finalPricePerKg : finalPrice;
    details.push({
      label: "Redondeo",
      value: `${roundingMeta.label} con umbral ${roundingMeta.threshold}: ${formatRecommendationMoney(rawPrice)} -> ${formatRecommendationMoney(roundedBasisPrice, 0)}`,
    });
  }

  if (isKgBasis && Number(snapshot.kgPerSaleUnit) > 0) {
    details.push({
      label: "Conversión a unidad de venta",
      value: `${formatRecommendationMoney(finalPricePerKg, 0)} x ${formatSignificantNumber(snapshot.kgPerSaleUnit)} kg = ${formatRecommendationMoney(finalPrice, 0)}`,
    });
  }

  details.push(
    { label: "Precio anterior", value: formatRecommendationMoney(oldPrice, 0) },
    ...(isKgBasis ? [{ label: "Precio final / kg", value: formatRecommendationMoney(finalPricePerKg, 0) }] : []),
    { label: "Precio final", value: formatRecommendationMoney(finalPrice, 0) }
  );

  return details;
};

const getRecommendationReferenceCostBreakdown = (item) => {
  const snapshot = getRecommendationCalculationSnapshot(item);
  return snapshot.referenceCostBreakdown && typeof snapshot.referenceCostBreakdown === "object"
    ? snapshot.referenceCostBreakdown
    : null;
};

const getReferenceCostListTypeLabel = (listType) =>
  listType === "importaciones" ? "Importación" : "Compra estándar";

const getReferenceCostAllocationBasisLabel = (basis) => {
  if (basis === "weight") return "peso";
  if (basis === "quantity") return "cantidad";
  if (basis === "volume") return "volumen";
  if (basis === "price") return "precio proveedor";
  if (basis === "importaciones") return "gastos de importación";
  return basis || "-";
};

const getReferenceCostResolutionLabel = (type) => {
  if (type === "price_unit_kg") return "Precio por kg";
  if (type === "unit_weight_kg") return "Peso de la unidad";
  if (type === "matching_stock_unit") return "Misma unidad";
  if (type === "unit_conversion") return "Conversión de unidad";
  if (type === "stored_reference_cost") return "Costo histórico guardado";
  if (type === "missing_conversion") return "Conversión pendiente";
  if (type === "unit_not_in_import") return "Sin compra en esta unidad";
  if (type === "missing_unit_weight") return "Peso de unidad pendiente";
  return type || "-";
};

const getReferenceCostSourceLabel = (breakdown) => {
  if (!breakdown) return "-";
  const name = breakdown.sourceStockListName || `Lista ${breakdown.sourceStockListId || "-"}`;
  const date = breakdown.sourceStockListCreatedAt
    ? ` · ${formatDateTimeDisplay(breakdown.sourceStockListCreatedAt)}`
    : "";
  return `${name}${date}`;
};

const getReferenceCostExpenseText = (breakdown) => {
  if (!breakdown) return "-";
  if (breakdown.listType === "importaciones" && Array.isArray(breakdown.expenses) && breakdown.expenses.length) {
    return breakdown.expenses
      .map((expense) => {
        const label = expense.title || `Gasto ${expense.expenseId || ""}`.trim();
        const basis = getReferenceCostAllocationBasisLabel(expense.allocationBasis);
        return `${label} (${basis}): ${formatRecommendationMoney(expense.expenseShare)}`;
      })
      .join(" · ");
  }
  const assigned = formatRecommendationMoney(breakdown.totalAllocatedExpenses);
  const total = formatRecommendationMoney(breakdown.totalListExpenses);
  const basis = getReferenceCostAllocationBasisLabel(breakdown.allocationBasis);
  const listBasis = Number(breakdown.listTotalBasis);
  const rowBasis = Array.isArray(breakdown.rows)
    ? breakdown.rows.reduce((sum, row) => sum + (Number(row.basisValue) || 0), 0)
    : 0;
  if (Number.isFinite(listBasis) && listBasis > 0 && rowBasis > 0) {
    return `${assigned} de ${total} por ${basis} (${formatSignificantNumber(rowBasis)} / ${formatSignificantNumber(listBasis)})`;
  }
  return `${assigned} de ${total} por ${basis}`;
};

const getReferenceCostFormulaText = (breakdown) => {
  const resolution = breakdown?.resolution || {};
  if (!breakdown) return "-";
  if (breakdown.listType === "importaciones" && resolution.type === "unit_conversion") {
    const conversion = resolution.conversion || {};
    const expected = Number(breakdown.costPerStockUnit) * (Number(conversion.fromAmount) / Number(conversion.toAmount));
    const formula = `${formatRecommendationMoney(breakdown.costPerStockUnit)} x (${formatSignificantNumber(conversion.fromAmount || 0)} / ${formatSignificantNumber(conversion.toAmount || 0)})`;
    if (!recommendationAmountsMatch(breakdown.referenceCostRaw, expected)) {
      return getStoredReferenceCostText(breakdown, expected, formula);
    }
    return `${formula} = ${formatRecommendationMoney(breakdown.referenceCostRaw)}`;
  }
  if (resolution.type === "stored_reference_cost") {
    return getStoredReferenceCostText(
      breakdown,
      resolution.expectedReferenceCostRaw,
      resolution.expectedFormula
    );
  }
  if (resolution.type === "unit_not_in_import") {
    return "La importación no compró esta unidad.";
  }
  if (breakdown.listType === "importaciones") {
    const expected = Number(breakdown.totalReferenceCost) / Number(breakdown.totalQuantity);
    const formula = `${formatRecommendationMoney(breakdown.totalReferenceCost)} / ${formatSignificantNumber(breakdown.totalQuantity || 0)} ${breakdown.stockUnit || "unidad"}`;
    if (!recommendationAmountsMatch(breakdown.referenceCostRaw, expected)) {
      return getStoredReferenceCostText(breakdown, expected, formula);
    }
    return `${formula} = ${formatRecommendationMoney(breakdown.referenceCostRaw)}`;
  }
  if (resolution.type === "unit_weight_kg") {
    const expected = Number(breakdown.costPerKg) * Number(breakdown.unitWeightKg);
    const formula = `${formatRecommendationMoney(breakdown.costPerKg)} x ${formatSignificantNumber(breakdown.unitWeightKg || 0)} kg`;
    if (!recommendationAmountsMatch(breakdown.referenceCostRaw, expected)) {
      return getStoredReferenceCostText(breakdown, expected, formula);
    }
    return `${formula} = ${formatRecommendationMoney(breakdown.referenceCostRaw)}`;
  }
  if (resolution.type === "price_unit_kg") {
    const expected = Number(breakdown.totalReferenceCost) / Number(breakdown.totalKg);
    const formula = `${formatRecommendationMoney(breakdown.totalReferenceCost)} / ${formatSignificantNumber(breakdown.totalKg || 0)} kg`;
    if (!recommendationAmountsMatch(breakdown.referenceCostRaw, expected)) {
      return getStoredReferenceCostText(breakdown, expected, formula);
    }
    return `${formula} = ${formatRecommendationMoney(breakdown.referenceCostRaw)}`;
  }
  if (resolution.type === "matching_stock_unit") {
    const unit = breakdown.priceMetricUnit || breakdown.stockMetricUnits?.[0] || "unidad";
    const expected = Number(breakdown.totalReferenceCost) / Number(breakdown.totalQuantity);
    const formula = `${formatRecommendationMoney(breakdown.totalReferenceCost)} / ${formatSignificantNumber(breakdown.totalQuantity || 0)} ${unit}`;
    if (!recommendationAmountsMatch(breakdown.referenceCostRaw, expected)) {
      return getStoredReferenceCostText(breakdown, expected, formula);
    }
    return `${formula} = ${formatRecommendationMoney(breakdown.referenceCostRaw)}`;
  }
  return resolution.formula || breakdown.conversionStatus || "-";
};

const getRecommendationReferenceCostDetails = (item) => {
  const breakdown = getRecommendationReferenceCostBreakdown(item);
  if (!breakdown) {
    return [
      {
        label: "Detalle costo",
        value: "Disponible para ejecuciones nuevas; esta recomendación conserva solo el costo final.",
      },
    ];
  }

  const details = [
    { label: "Fuente", value: getReferenceCostSourceLabel(breakdown) },
    { label: "Tipo", value: getReferenceCostListTypeLabel(breakdown.listType) },
    { label: "Compra base", value: formatRecommendationMoney(breakdown.totalBaseCost) },
    { label: "Gastos asignados", value: getReferenceCostExpenseText(breakdown) },
    { label: "Costo total variedad", value: formatRecommendationMoney(breakdown.totalReferenceCost) },
    { label: "Método unidad", value: getReferenceCostResolutionLabel(breakdown.resolution?.type) },
    { label: "Formula costo", value: getReferenceCostFormulaText(breakdown) },
    { label: "Costo usado", value: formatRecommendationMoney(breakdown.referenceCostRaw ?? breakdown.referenceCost) },
  ];

  if (breakdown.listType === "importaciones") {
    details.splice(5, 0, {
      label: "Unidad compra",
      value: `${formatSignificantNumber(breakdown.totalQuantity || 0)} ${breakdown.stockUnit || "unidad"}`,
    });
  } else if (breakdown.allocationBasis) {
    details.splice(5, 0, {
      label: "Base gastos",
      value: getReferenceCostAllocationBasisLabel(breakdown.allocationBasis),
    });
  }

  return details;
};

const resolveMetricUnitInputValue = (value) => {
  const trimmed = String(value || "").trim();
  if (!trimmed) return "";
  const normalized = trimmed.toLowerCase();
  const knownOption = STOCK_METRIC_UNIT_OPTIONS.find((option) => option.value === normalized);
  return knownOption ? knownOption.value : trimmed;
};

const buildMetricUnitOptions = (value) => {
  const resolved = resolveMetricUnitInputValue(value);
  if (!resolved) return STOCK_METRIC_UNIT_OPTIONS;
  const exists = STOCK_METRIC_UNIT_OPTIONS.some((option) => option.value === resolved);
  if (exists) return STOCK_METRIC_UNIT_OPTIONS;
  return [{ value: resolved, label: resolved }, ...STOCK_METRIC_UNIT_OPTIONS];
};

export const createStockEditForm = (stock, { isImportList = false } = {}) => {
  const standardGranel = isStandardGranelPurchase({
    isImportList,
    purchaseMode: stock?.purchaseMode,
    metricUnit: stock?.metricUnit,
  });
  const hasExplicitGranelWeight = getExplicitStandardGranelWeight(stock, { isImportList }) !== null;
  const legacyQuantity = Number(stock?.quantity);
  const legacyAmountMultiplier = standardGranel
    && !hasExplicitGranelWeight
    && Number.isFinite(legacyQuantity)
    && legacyQuantity > 0
    ? legacyQuantity
    : 1;
  const normalizeAmount = (value) => {
    if (value === undefined || value === null) return "";
    const parsed = Number(value);
    return Number.isFinite(parsed) ? String(parsed * legacyAmountMultiplier) : String(value);
  };

  return {
    price: normalizeAmount(stock?.price),
    priceWithoutIva: normalizeAmount(stock?.priceWithoutIva),
    quantity: standardGranel
      ? "1"
      : stock?.quantity !== undefined && stock?.quantity !== null
        ? String(stock.quantity)
        : "",
    purchaseMode: isImportList ? null : stock?.purchaseMode ?? null,
    purchaseVarietyPriceId: stock?.purchaseVarietyPriceId ?? null,
    ...stockRegistryDraft(stock),
    metricUnit: stock?.purchaseSnapshot?.metricUnit || resolveMetricUnitInputValue(stock?.metricUnit),
    unitsPerPack: stock?.unitsPerPack ? String(stock.unitsPerPack) : "",
    weight: isFormatStockPurchase(stock) && stock?.purchaseSnapshot?.weightKg != null
      ? String(stock.purchaseSnapshot.weightKg)
      : standardGranel
      ? String(getStandardGranelDisplayWeight(stock, { isImportList }) || "")
      : stock?.weightKg !== undefined && stock?.weightKg !== null
        ? String(stock.weightKg)
        : stock?.weight !== undefined && stock?.weight !== null
          ? String(stock.weight)
          : "",
    lengthCm: stock?.lengthCm !== undefined && stock?.lengthCm !== null ? String(stock.lengthCm) : "",
    widthCm: stock?.widthCm !== undefined && stock?.widthCm !== null ? String(stock.widthCm) : "",
    heightCm: stock?.heightCm !== undefined && stock?.heightCm !== null ? String(stock.heightCm) : "",
    bultosPerBin:
      stock?.bultosPerBin !== undefined && stock?.bultosPerBin !== null
        ? String(stock.bultosPerBin)
        : "",
    paymentMethod: stock?.paymentMethod || "efectivo",
    providerId: stock?.providerId ? String(stock.providerId) : "",
  };
};

export const resolveStockEditPurchaseValues = ({
  stock,
  form,
  isImportList = false,
} = {}) => {
  const metricUnit = String(form?.metricUnit || "").trim();
  const parsedWeight = String(form?.weight ?? "").trim() === ""
    ? null
    : Number(form.weight);
  const proposedStandardGranel = isStandardGranelPurchase({ isImportList, metricUnit, purchaseMode: form?.purchaseMode });
  if (!proposedStandardGranel) {
    return {
      quantity: Number(form?.quantity),
      weight: parsedWeight,
      usesExplicitGranelWeight: false,
    };
  }

  return {
    quantity: 1,
    weight: parsedWeight,
    usesExplicitGranelWeight: true,
  };
};

const StockListViewPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("productos");
  const [stockItems, setStockItems] = useState([]);
  const [warehousePackQuantities, setWarehousePackQuantities] = useState({});
  const [expenses, setExpenses] = useState([]);
  const [expenseTarget, setExpenseTarget] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingWholesale, setLoadingWholesale] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [formatRegistrationStock, setFormatRegistrationStock] = useState(null);
  const [stockList, setStockList] = useState(null);
  const [wholesalePriceMap, setWholesalePriceMap] = useState({});
  const [varietyPriceUnitMap, setVarietyPriceUnitMap] = useState({});
  const [importConversions, setImportConversions] = useState([]);
  const [importConversionsError, setImportConversionsError] = useState("");
  const [wholesaleError, setWholesaleError] = useState("");
  const productsCacheRef = useRef([]);
  const [paymentForm, setPaymentForm] = useState(() => ({
    amount: "",
    method: "efectivo",
    reference: "",
    paidAt: getTodayDate(),
    voucherFile: null,
  }));
  const [paymentError, setPaymentError] = useState("");
  const { confirm } = useConfirmDialog();
  const [isSavingPayment, setIsSavingPayment] = useState(false);
  const [isDeletingStock, setIsDeletingStock] = useState(false);
  const [deletingStockId, setDeletingStockId] = useState(null);
  const [isEditingStock, setIsEditingStock] = useState(false);
  const [stockEditForm, setStockEditForm] = useState(() => createStockEditForm(null));
  const [stockEditError, setStockEditError] = useState("");
  const [isSavingStockEdit, setIsSavingStockEdit] = useState(false);
  const [providersLoading, setProvidersLoading] = useState(false);
  const [providersLoaded, setProvidersLoaded] = useState(false);
  const [providersError, setProvidersError] = useState("");
  const [editProviders, setEditProviders] = useState([]);
  const [editProviderSearchTerm, setEditProviderSearchTerm] = useState("");
  const [isEditProviderSelectOpen, setIsEditProviderSelectOpen] = useState(false);
  const [estimateEquivalences, setEstimateEquivalences] = useState([]);
  const [confirmedEstimateStockIds, setConfirmedEstimateStockIds] = useState([]);
  const [loadingEstimateEquivalences, setLoadingEstimateEquivalences] = useState(false);
  const [loadingEstimateConfirmations, setLoadingEstimateConfirmations] = useState(false);
  const [estimateReviewError, setEstimateReviewError] = useState("");
  const [estimateActionError, setEstimateActionError] = useState("");
  const [isSavingEstimateConfirmation, setIsSavingEstimateConfirmation] = useState(false);
  const [isSavingEquivalence, setIsSavingEquivalence] = useState(false);
  const [deletingEquivalenceId, setDeletingEquivalenceId] = useState(null);
  const [recommendationRuns, setRecommendationRuns] = useState([]);
  const [recommendationLoading, setRecommendationLoading] = useState(false);
  const [recommendationActionLoading, setRecommendationActionLoading] = useState(false);
  const [recommendationError, setRecommendationError] = useState("");
  const [recommendationPage, setRecommendationPage] = useState(1);
  const [expandedRecommendationItemId, setExpandedRecommendationItemId] = useState(null);
  const recommendationNoticeRunIdsRef = useRef(new Set());
  const [activationMode, setActivationMode] = useState("now");
  const [activationScheduleInput, setActivationScheduleInput] = useState("");
  const [activationScheduleError, setActivationScheduleError] = useState("");
  const [activationScheduleSaving, setActivationScheduleSaving] = useState(false);
  const [equivalenceModal, setEquivalenceModal] = useState({
    isOpen: false,
    stockId: null,
    fromUnit: "",
    toUnit: "",
    fromAmount: "1",
    toAmount: "",
    error: "",
  });
  const branchId = useParams().branchId || localStorage.getItem('selectedBranchId');
  const scopeKey = `${branchId}:${id}`;
  const activeScopeRef = useRef(scopeKey);
  activeScopeRef.current = scopeKey;
  const { user } = useCurrentUser();
  const canManageFormats = hasPermission(user, 'stock.formats.manage');
  const pendingFormats = stockItems.filter((stock) => ['pending', 'changes_requested'].includes(stock.formatReviewStatus));
  const canCreateStock = hasPermission(user, "stock.create");
  const canUpdateStock = hasPermission(user, "stock.update");
  const canUpdateProduct = hasPermission(user, "product.update");
  const canUpdatePrice = hasPermission(user, "price.update");
  const canDeleteStock = hasPermission(user, "stock.delete");
  const isImportList = (stockList?.listType || "standard") === "importaciones";
  const editProviderSelectRef = useRef(null);

  const stockReadGeneration = useRef(0);
  const refreshStockListItems = useCallback(async (selectedStockId = null, stockIds) => {
    const requestScope = `${branchId}:${id}`;
    const generation = ++stockReadGeneration.current;
    const stockListData = await getStockList(branchId, id, stockIds?.length ? { stockIds: stockIds.join(',') } : {});
    if (activeScopeRef.current !== requestScope || generation !== stockReadGeneration.current) return null;
    const nextStocks = stockListData?.Stocks || [];
    setStockList((previous) => shareCatalogValue(previous, { ...stockListData, Stocks: mergeCatalogDelta(previous?.Stocks || [], nextStocks, stockListData?.deltaStockIds) }));
    setStockItems((previous) => mergeCatalogDelta(previous, nextStocks, stockListData?.deltaStockIds));
    if (Array.isArray(stockListData?.Expenses)) {
      setExpenses((previous) => shareCatalogValue(previous, stockListData.Expenses));
    }
    if (selectedStockId !== null && selectedStockId !== undefined) {
      const refreshedItem = nextStocks.find((item) => Number(item.id) === Number(selectedStockId)) || null;
      setSelectedItem(refreshedItem);
      return refreshedItem;
    }
    return null;
  }, [branchId, id]);

  const loadEstimateEquivalences = useCallback(async () => {
    if (!branchId) return;
    setLoadingEstimateEquivalences(true);
    setEstimateReviewError("");
    try {
      const data = await getEstimateUnitEquivalences(branchId);
      setEstimateEquivalences(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching estimate unit equivalences:", error);
      setEstimateReviewError("No se pudieron cargar las equivalencias de unidades.");
    } finally {
      setLoadingEstimateEquivalences(false);
    }
  }, [branchId]);

  const loadEstimateConfirmations = useCallback(async () => {
    if (!branchId || !id) return;
    setLoadingEstimateConfirmations(true);
    setEstimateReviewError("");
    try {
      const data = await getStockEstimateConfirmations(branchId, id);
      setConfirmedEstimateStockIds(Array.isArray(data?.confirmedStockIds) ? data.confirmedStockIds : []);
    } catch (error) {
      console.error("Error fetching estimate confirmations:", error);
      setEstimateReviewError("No se pudieron cargar las confirmaciones del estimado.");
    } finally {
      setLoadingEstimateConfirmations(false);
    }
  }, [branchId, id]);

  const loadRecommendationRuns = useCallback(async () => {
    if (!branchId || !id) return;
    setRecommendationLoading(true);
    setRecommendationError("");
    try {
      const data = await getStockListPriceRecommendationRuns(branchId, id);
      setRecommendationRuns(Array.isArray(data?.data) ? data.data : []);
    } catch (error) {
      console.error("Error fetching price recommendation runs:", error);
      setRecommendationError("No se pudieron cargar las recomendaciones de precios.");
    } finally {
      setRecommendationLoading(false);
    }
  }, [branchId, id]);

  const notifyRecommendationCompleted = useCallback((run) => {
    const runId = run?.id;
    if (runId && recommendationNoticeRunIdsRef.current.has(Number(runId))) return;
    if (runId) recommendationNoticeRunIdsRef.current.add(Number(runId));
    notify(run?.autoUpdatePrices === false ? "Recomendaciones listas." : "Precios actualizados.");
  }, []);

  const applyActivationResponse = useCallback(async (result) => {
    if (result?.stockList) {
      setStockList((prev) => ({ ...(prev || {}), ...result.stockList }));
    }
    const run = result?.recommendationRun || result?.activationResult?.recommendationRun || null;
    if (run) {
      setRecommendationRuns((prev) => [
        run,
        ...prev.filter((entry) => Number(entry.id) !== Number(run.id)),
      ]);
    }
    await loadRecommendationRuns();
    const activationStatus = result?.activationResult?.status;
    if (activationStatus === "failed") {
      setRecommendationError(result?.activationResult?.error || "La activación automática falló.");
    }
    return { run, activationStatus };
  }, [loadRecommendationRuns]);

  useEffect(() => {
    setActivationScheduleInput(formatDateTimeLocal(stockList?.autoActivationScheduledAt));
    setActivationScheduleError("");
  }, [stockList?.autoActivationScheduledAt]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setExpenses([]);
    setExpenseTarget(null);
    const fetchStockListData = async () => {
      try {
        await refreshStockListItems();
        const expenseData = await getExpensesByStockList(branchId, id);
        if (active) setExpenses(expenseData || []);
      } catch (error) {
        console.error("Error fetching stock list:", error);
      } finally {
        if (active) setLoading(false);
      }
    };

    fetchStockListData();
    return () => { active = false; };
  }, [branchId, id, refreshStockListItems]);

  const buildVarietyPriceUnitMap = useCallback((productList) => {
    const unitMap = {};
    (productList || []).forEach((product) => {
      (product.Varieties || []).forEach((variety) => {
        const seen = new Set();
        const units = [];
        (variety.VarietyPrices || []).forEach((price) => {
          const label = String(price?.metricUnit || "").trim();
          const key = toMetricUnitKey(label);
          if (!label || !key || seen.has(key)) return;
          seen.add(key);
          units.push({ key, label });
        });
        unitMap[variety.id] = units;
      });
    });
    return unitMap;
  }, []);

  const fetchWholesalePrices = useCallback(async (event) => {
    const requestScope = `${branchId}:${id}`;
    if (!branchId) return;
    setLoadingWholesale(true);
    setWholesaleError("");
    try {
      const [data, presentations] = await Promise.all([
        fetchCatalogDelta((params) => getProducts(branchId, { pageSize: 0, ...params }), event, productsCacheRef.current),
        fetchCatalogDelta((params) => Object.keys(params).length ? getProductPresentations(branchId, params) : getProductPresentations(branchId), event, productsCacheRef.current),
      ]);
      if (activeScopeRef.current !== requestScope) return;
      const productsData = mergeCatalogDelta(productsCacheRef.current, mergeStockPurchaseCatalog(
        normalizePaginatedResponse(data).data, normalizePaginatedResponse(presentations).data
      ), data?.deltaProductIds);
      productsCacheRef.current = productsData;
      setWholesalePriceMap(buildWholesalePriceMap(productsData));
      setVarietyPriceUnitMap(buildVarietyPriceUnitMap(productsData));
    } catch (error) {
      console.error("Error fetching wholesale prices:", error);
      setWholesaleError("No se pudo cargar el catálogo de formatos y precios.");
      if (event) throw error;
    } finally {
      setLoadingWholesale(false);
    }
  }, [branchId, id, buildVarietyPriceUnitMap]);

  const scheduleStockRefresh = useCatalogUpdateQueue(`${branchId}:${id}`, async (event) => {
    const work = [fetchWholesalePrices(event)];
    if (event.stockListIds?.some((listId) => String(listId) === String(id))) work.push(refreshStockListItems(null, event.stockIds));
    await Promise.all(work);
  });
  useProductEvents(branchId, scheduleStockRefresh);

  const handleRecommendationEvent = useCallback((event) => {
    const type = String(event?.type || "");
    if (!["price-recommendation-run.completed", "price-recommendation-run.failed"].includes(type)) return;
    if (event?.stockListId && String(event.stockListId) !== String(id)) return;
    loadRecommendationRuns();
    if (type === "price-recommendation-run.completed") {
      notifyRecommendationCompleted(event);
    }
    if (type === "price-recommendation-run.failed") {
      setRecommendationError(event?.error || "La ejecucion de recomendaciones fallo.");
    }
  }, [id, loadRecommendationRuns, notifyRecommendationCompleted]);

  usePurchaseEvents(branchId, handleRecommendationEvent, { enabled: !!branchId && !!id });

  useEffect(() => {
    fetchWholesalePrices();
  }, [fetchWholesalePrices]);

  useEffect(() => {
    loadEstimateEquivalences();
  }, [loadEstimateEquivalences]);

  useEffect(() => {
    loadEstimateConfirmations();
  }, [loadEstimateConfirmations]);

  useEffect(() => {
    loadRecommendationRuns();
  }, [loadRecommendationRuns]);

  useEffect(() => {
    const hasRunningRun = recommendationRuns.some((run) => run?.status === "running");
    if (!hasRunningRun) return undefined;
    const timer = window.setInterval(() => {
      loadRecommendationRuns();
    }, 5000);
    return () => window.clearInterval(timer);
  }, [recommendationRuns, loadRecommendationRuns]);

  useEffect(() => {
    const loadImportConversions = async () => {
      if (!branchId || !isImportList || !stockItems.length) {
        setImportConversions([]);
        setImportConversionsError("");
        return;
      }
      const varietyIds = Array.from(new Set(
        stockItems
          .map((item) => Number(item?.varietyId))
          .filter((value) => Number.isInteger(value))
      ));
      if (!varietyIds.length) {
        setImportConversions([]);
        return;
      }
      try {
        const data = await getImportConversions(branchId, varietyIds);
        setImportConversions(Array.isArray(data?.data) ? data.data : []);
        setImportConversionsError("");
      } catch (error) {
        console.error("Error fetching import conversions:", error);
        setImportConversionsError("No se pudieron cargar las conversiones de importación.");
      }
    };
    loadImportConversions();
  }, [branchId, isImportList, stockItems]);


  const paymentMethods = STOCK_PAYMENT_METHODS;

  const loadEditProviders = useCallback(async () => {
    if (providersLoaded || providersLoading) return;
    setProvidersLoading(true);
    setProvidersError("");
    try {
      const data = await getProviders({ includeDeleted: true, pageSize: 0 });
      const normalized = normalizePaginatedResponse(data);
      setEditProviders(Array.isArray(normalized.data) ? normalized.data : []);
      setProvidersLoaded(true);
    } catch (error) {
      console.error("Error fetching providers for stock editing:", error);
      setProvidersError("No se pudieron cargar los proveedores.");
    } finally {
      setProvidersLoading(false);
    }
  }, [providersLoaded, providersLoading]);

  const estimateEquivalenceMap = useMemo(() => {
    const map = new Map();
    (estimateEquivalences || []).forEach((entry) => {
      const fromUnitKey = toMetricUnitKey(entry?.fromUnitKey || entry?.fromUnit);
      const toUnitKey = toMetricUnitKey(entry?.toUnitKey || entry?.toUnit);
      const fromAmountRaw = Number(entry?.fromAmount);
      const toAmountRaw = Number(entry?.toAmount);
      const factorRaw = Number(entry?.factor);
      const fromAmount =
        Number.isFinite(fromAmountRaw) && fromAmountRaw > 0
          ? fromAmountRaw
          : 1;
      const toAmount =
        Number.isFinite(toAmountRaw) && toAmountRaw > 0
          ? toAmountRaw
          : (Number.isFinite(factorRaw) && factorRaw > 0 ? factorRaw : null);
      if (!fromUnitKey || !toUnitKey) return;
      if (!Number.isFinite(fromAmount) || fromAmount <= 0) return;
      if (!Number.isFinite(toAmount) || toAmount <= 0) return;
      const forwardMultiplier = toAmount / fromAmount;
      const reverseMultiplier = fromAmount / toAmount;
      if (!Number.isFinite(forwardMultiplier) || forwardMultiplier <= 0) return;
      if (!Number.isFinite(reverseMultiplier) || reverseMultiplier <= 0) return;

      const fromLabel = String(entry?.fromUnit || entry?.fromUnitKey || fromUnitKey).trim() || fromUnitKey;
      const toLabel = String(entry?.toUnit || entry?.toUnitKey || toUnitKey).trim() || toUnitKey;

      const forwardList = map.get(fromUnitKey) || [];
      forwardList.push({
        ...entry,
        sourceUnitKey: fromUnitKey,
        sourceUnit: fromLabel,
        sourceAmount: fromAmount,
        targetUnitKey: toUnitKey,
        targetUnit: toLabel,
        targetAmount: toAmount,
        multiplier: forwardMultiplier,
        fromUnitKey,
        toUnitKey,
        fromAmount,
        toAmount,
        direction: "forward",
      });
      map.set(fromUnitKey, forwardList);

      const reverseList = map.get(toUnitKey) || [];
      reverseList.push({
        ...entry,
        sourceUnitKey: toUnitKey,
        sourceUnit: toLabel,
        sourceAmount: toAmount,
        targetUnitKey: fromUnitKey,
        targetUnit: fromLabel,
        targetAmount: fromAmount,
        multiplier: reverseMultiplier,
        fromUnitKey,
        toUnitKey,
        fromAmount,
        toAmount,
        direction: "reverse",
      });
      map.set(toUnitKey, reverseList);
    });
    return map;
  }, [estimateEquivalences]);

  const confirmedEstimateSet = useMemo(
    () => new Set((confirmedEstimateStockIds || []).map((value) => Number(value)).filter((value) => Number.isFinite(value))),
    [confirmedEstimateStockIds]
  );

  const resolveWholesaleUnitOptions = useCallback((item) => {
    const entries = wholesalePriceMap[item?.varietyId] || [];
    const sourceKey = toMetricUnitKey(item?.metricUnit);
    const options = [];
    const seen = new Set();
    entries.forEach((entry) => {
      const unitLabel = String(entry?.metricUnit || "").trim();
      const unitKey = toMetricUnitKey(unitLabel);
      if (!unitLabel || !unitKey) return;
      if (unitKey === sourceKey) return;
      if (seen.has(unitKey)) return;
      seen.add(unitKey);
      options.push({ key: unitKey, label: unitLabel });
    });
    return options;
  }, [wholesalePriceMap]);

  const getPaymentSummary = (stock) => {
    const calculatedAmounts = calculateStockAmounts(stock);
    const canCalculateTotals = stock?.price !== undefined && stock?.quantity !== undefined;
    const totalAmount = canCalculateTotals
      ? calculatedAmounts.providerTotalAmount
      : resolveProviderTotalAmount(stock);
    const stockCostAmount = canCalculateTotals
      ? calculatedAmounts.stockCostAmount
      : resolveStockCostAmount(stock);
    const ivaCostUpliftAmount = canCalculateTotals
      ? calculatedAmounts.ivaCostUpliftAmount
      : resolveIvaCostUpliftAmount(stock);
    const paidAmount = (stock.StockPayments || []).reduce(
      (sum, payment) => sum + Number(payment.amount || 0),
      0
    );
    const balance = Math.max(totalAmount - paidAmount, 0);
    const paymentStatus =
      paidAmount <= 0 ? "unpaid" : paidAmount < totalAmount ? "partial" : "paid";
    return {
      totalAmount,
      providerTotalAmount: totalAmount,
      stockCostAmount,
      ivaCostUpliftAmount,
      paidAmount,
      balance,
      paymentStatus,
    };
  };

  const resetPaymentForm = () => {
    setPaymentForm({ amount: "", method: "efectivo", reference: "", paidAt: getTodayDate(), voucherFile: null });
    setPaymentError("");
  };

  useEffect(() => {
    if (selectedItem && activeTab === "productos") {
      resetPaymentForm();
    }
  }, [selectedItem, activeTab]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        editProviderSelectRef.current &&
        !editProviderSelectRef.current.contains(event.target)
      ) {
        setIsEditProviderSelectOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handlePaymentChange = (field, value) => {
    setPaymentForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handlePaymentVoucherChange = (file) => {
    setPaymentForm((prev) => ({ ...prev, voucherFile: file || null }));
  };

  const handleAddPayment = async () => {
    if (!canUpdateStock) return;
    if (!selectedItem) return;
    setPaymentError("");
    const amount = Number(paymentForm.amount);
    if (!Number.isFinite(amount) || amount <= 0) {
      setPaymentError("Monto invalido.");
      return;
    }
    if (!paymentForm.method) {
      setPaymentError("Selecciona un metodo de pago.");
      return;
    }
    const summary = getPaymentSummary(selectedItem);
    if (amount > summary.balance) {
      setPaymentError("El monto supera lo pendiente.");
      return;
    }
    setIsSavingPayment(true);
    try {
      let voucherUrl;
      if (paymentForm.voucherFile) {
        const response = await uploadFile(paymentForm.voucherFile);
        voucherUrl = response?.url;
      }
      const payload = {
        amount,
        method: paymentForm.method,
        reference: paymentForm.reference || undefined,
        paidAt: paymentForm.paidAt || undefined,
      };
      if (voucherUrl) {
        payload.voucherUrl = voucherUrl;
      }
      const created = await addStockPayment(branchId, selectedItem.id, payload);
      const updatedPayments = [...(selectedItem.StockPayments || []), created];
      const summary = getPaymentSummary({ ...selectedItem, StockPayments: updatedPayments });
      const updatedItem = {
        ...selectedItem,
        StockPayments: updatedPayments,
        paidAmount: summary.paidAmount,
        balance: summary.balance,
        paymentStatus: summary.paymentStatus,
      };
      setSelectedItem(updatedItem);
      setStockItems((prev) =>
        prev.map((item) => (item.id === selectedItem.id ? updatedItem : item))
      );
      resetPaymentForm();
    } catch (error) {
      console.error(error);
      setPaymentError("Error al registrar pago.");
    } finally {
      setIsSavingPayment(false);
    }
  };

  const handleCloseModal = () => {
    handleCloseEquivalenceModal();
    setEstimateActionError("");
    setIsEditingStock(false);
    setStockEditForm(createStockEditForm(null));
    setStockEditError("");
    setEditProviderSearchTerm("");
    setIsEditProviderSelectOpen(false);
    setSelectedItem(null);
    resetPaymentForm();
  };

  const handleStartEditingStock = () => {
    if (!canUpdateStock || !selectedItem || activeTab !== "productos") return;
    setIsEditingStock(true);
    setStockEditForm(createStockEditForm(selectedItem, { isImportList }));
    setStockEditError("");
    setEditProviderSearchTerm("");
    setIsEditProviderSelectOpen(false);
    loadEditProviders();
  };

  const handleCancelEditingStock = () => {
    setIsEditingStock(false);
    setStockEditForm(createStockEditForm(selectedItem, { isImportList }));
    setStockEditError("");
    setEditProviderSearchTerm("");
    setIsEditProviderSelectOpen(false);
  };

  const handleStockEditChange = (field, value) => {
    setStockEditError("");
    setStockEditForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleStockEditMetricUnitChange = (value) => {
    setStockEditError("");
    setStockEditForm((prev) => {
      const entersStandardGranel = !isStandardGranelPurchase({
        isImportList,
        metricUnit: prev.metricUnit,
      }) && isStandardGranelPurchase({ isImportList, metricUnit: value });
      if (!entersStandardGranel) {
        return {
          ...prev,
          metricUnit: value,
          bultosPerBin: isBinUnit(value) ? prev.bultosPerBin : "",
        };
      }
      const parsedQuantity = Number(prev.quantity);
      const multiplier = Number.isFinite(parsedQuantity) && parsedQuantity > 0
        ? parsedQuantity
        : 1;
      const multiplyValue = (entry) => {
        if (String(entry ?? "").trim() === "") return entry;
        const parsed = Number(entry);
        return Number.isFinite(parsed) ? String(parsed * multiplier) : entry;
      };
      return {
        ...prev,
        price: multiplyValue(prev.price),
        priceWithoutIva: multiplyValue(prev.priceWithoutIva),
        quantity: "1",
        metricUnit: value,
        weight: multiplyValue(prev.weight),
        bultosPerBin: "",
      };
    });
  };

  const handleSelectEditProvider = (providerId) => {
    setStockEditError("");
    setStockEditForm((prev) => ({
      ...prev,
      providerId: providerId ? String(providerId) : "",
    }));
    setEditProviderSearchTerm("");
    setIsEditProviderSelectOpen(false);
  };

  const handleSaveStockEdit = async () => {
    if (!canUpdateStock || !selectedItem || activeTab !== "productos" || isSavingStockEdit) return;

    const price = Number(stockEditForm.price);
    if (!Number.isFinite(price) || price < 0) {
      setStockEditError("Ingresa un precio con IVA válido.");
      return;
    }

    const priceWithoutIva = Number(stockEditForm.priceWithoutIva || 0);
    if (!Number.isFinite(priceWithoutIva) || priceWithoutIva < 0) {
      setStockEditError("Ingresa un precio sin IVA válido.");
      return;
    }

    if (price + priceWithoutIva <= 0) {
      setStockEditError("Ingresa al menos un precio mayor a 0.");
      return;
    }

    const metricUnit = String(stockEditForm.metricUnit || "").trim();
    const usesFormatSelector = !isImportList && stockEditForm.purchaseMode != null;
    const preserveLegacyGranel = stockList.status === 'completed' && selectedItem.purchaseMode === 'granel'
      && !selectedItem.sellingSnapshot && stockEditForm.purchaseMode === 'granel'
      && !stockEditForm.sellingVarietyPriceId && !stockEditForm.sellingOther && !stockEditForm.sellingWeightKg;
    if (usesFormatSelector) {
      const formatError = getPurchaseFormatError(stockEditForm, {
        allowLegacyGranel: preserveLegacyGranel,
        allowUnlinkedPack: selectedItem.purchaseMode === 'pack' && !selectedItem.purchaseVarietyPriceId
          && Boolean(selectedItem.purchaseSnapshot) && stockEditForm.purchaseMode === 'pack' && !stockEditForm.purchaseVarietyPriceId,
      });
      if (formatError) { setStockEditError(formatError); return; }
    }
    const packError = getStockPackError(stockEditForm);
    if (packError) { setStockEditError(packError); return; }
    if (!metricUnit) {
      setStockEditError("Selecciona una unidad métrica.");
      return;
    }

    if (!stockEditForm.providerId) {
      setStockEditError("Selecciona un proveedor.");
      return;
    }

    if (!stockEditForm.paymentMethod) {
      setStockEditError("Selecciona un método de pago.");
      return;
    }

    const weightValue = String(stockEditForm.weight || "").trim();
    const parsedWeight = weightValue === "" ? null : Number(weightValue);
    if (weightValue !== "" && !Number.isFinite(parsedWeight)) {
      setStockEditError("Ingresa un peso válido.");
      return;
    }
    const standardWeightError = getStandardStockWeightError({
      isImportList,
      purchaseMode: stockEditForm.purchaseMode,
      metricUnit,
      weight: stockEditForm.weight,
    });
    if (standardWeightError) {
      setStockEditError(standardWeightError);
      return;
    }
    const purchaseValues = resolveStockEditPurchaseValues({
      stock: selectedItem,
      form: stockEditForm,
      isImportList,
    });
    const quantity = purchaseValues.quantity;
    if (!Number.isFinite(quantity) || quantity <= 0) {
      setStockEditError("Ingresa una cantidad válida mayor a 0.");
      return;
    }
    const parsedLengthCm = String(stockEditForm.lengthCm || "").trim() === "" ? null : Number(stockEditForm.lengthCm);
    const parsedWidthCm = String(stockEditForm.widthCm || "").trim() === "" ? null : Number(stockEditForm.widthCm);
    const parsedHeightCm = String(stockEditForm.heightCm || "").trim() === "" ? null : Number(stockEditForm.heightCm);
    if (isImportList && !selectedItem?.categoryIsResaleOnly) {
      if (!Number.isFinite(parsedWeight) || parsedWeight <= 0) {
        setStockEditError("Ingresa el peso de importación en kg.");
        return;
      }
      if (
        !Number.isFinite(parsedLengthCm) ||
        !Number.isFinite(parsedWidthCm) ||
        !Number.isFinite(parsedHeightCm) ||
        parsedLengthCm <= 0 ||
        parsedWidthCm <= 0 ||
        parsedHeightCm <= 0
      ) {
        setStockEditError("Ingresa largo, ancho y alto de importación en cm.");
        return;
      }
    }

    let parsedBultosPerBin = null;
    if (!usesFormatSelector && isBinUnit(metricUnit)) {
      parsedBultosPerBin = Number(stockEditForm.bultosPerBin);
      if (!Number.isInteger(parsedBultosPerBin) || parsedBultosPerBin <= 0) {
        setStockEditError("Ingresa los bultos por bin.");
        return;
      }
    }

    setIsSavingStockEdit(true);
    setStockEditError("");
    try {
      await updateStock(branchId, selectedItem.id, {
        price,
        priceWithoutIva,
        quantity,
        metricUnit,
        weight: purchaseValues.weight,
        weightKg: isImportList || purchaseValues.usesExplicitGranelWeight
          ? purchaseValues.weight
          : undefined,
        lengthCm: isImportList ? parsedLengthCm : undefined,
        widthCm: isImportList ? parsedWidthCm : undefined,
        heightCm: isImportList ? parsedHeightCm : undefined,
        bultosPerBin: !usesFormatSelector && isBinUnit(metricUnit) ? parsedBultosPerBin : null,
        unitsPerPack: !usesFormatSelector && isStockPack(metricUnit) ? Number(stockEditForm.unitsPerPack) : null,
        ...(usesFormatSelector ? stockPurchaseFields(stockEditForm, { preserveLegacyGranel }) : {}),
        paymentMethod: stockEditForm.paymentMethod,
        providerId: Number(stockEditForm.providerId),
      });
      const refreshedItem = await refreshStockListItems(selectedItem.id);
      setIsEditingStock(false);
      setStockEditForm(createStockEditForm(refreshedItem, { isImportList }));
      setEditProviderSearchTerm("");
      setIsEditProviderSelectOpen(false);
      notify("Compra actualizada.");
    } catch (error) {
      console.error("Error updating stock item:", error);
      setStockEditError(
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "No se pudo actualizar la compra."
      );
    } finally {
      setIsSavingStockEdit(false);
    }
  };

  const handleDeleteStockItem = async () => {
    if (!canDeleteStock) return;
    if (!selectedItem) return;
    const label = selectedItem.varietyName || "producto";
    const approved = await confirm({
      title: 'Eliminar compra',
      message: `Borrar compra de ${label}?`,
      confirmLabel: 'Eliminar',
      cancelLabel: 'Cancelar',
      tone: 'danger',
    });
    if (!approved) return;
    setIsDeletingStock(true);
    try {
      await deleteStock(branchId, selectedItem.id);
      setStockItems((prev) => prev.filter((item) => item.id !== selectedItem.id));
      setConfirmedEstimateStockIds((prev) =>
        prev.filter((entryId) => Number(entryId) !== Number(selectedItem.id))
      );
      if (equivalenceModal.stockId && Number(equivalenceModal.stockId) === Number(selectedItem.id)) {
        handleCloseEquivalenceModal();
      }
      setSelectedItem(null);
    } catch (error) {
      console.error("Error deleting stock item:", error);
      notify("Error al eliminar la compra.");
    } finally {
      setIsDeletingStock(false);
    }
  };

  const handleDeleteStockFromList = async (event, stock) => {
    event.stopPropagation();
    if (!canDeleteStock) return;
    if (!stock) return;
    const label = stock.varietyName || "producto";
    const approved = await confirm({
      title: 'Eliminar compra',
      message: `Borrar compra de ${label}?`,
      confirmLabel: 'Eliminar',
      cancelLabel: 'Cancelar',
      tone: 'danger',
    });
    if (!approved) return;
    setDeletingStockId(stock.id);
    try {
      await deleteStock(branchId, stock.id);
      setStockItems((prev) => prev.filter((item) => item.id !== stock.id));
      setConfirmedEstimateStockIds((prev) =>
        prev.filter((entryId) => Number(entryId) !== Number(stock.id))
      );
      if (equivalenceModal.stockId && Number(equivalenceModal.stockId) === Number(stock.id)) {
        handleCloseEquivalenceModal();
      }
      if (selectedItem?.id === stock.id) {
        setSelectedItem(null);
      }
    } catch (error) {
      console.error("Error deleting stock item:", error);
      notify("Error al eliminar la compra.");
    } finally {
      setDeletingStockId(null);
    }
  };

  const handleFinalizeStockList = async () => {
    if (!canUpdateStock || recommendationActionLoading) return;
    if (!isStockListCompleted && pendingFormats.length) { notify('Revisa los formatos pendientes antes de finalizar la lista.'); return; }
    let payload = { autoActivationMode: "now" };
    if (activationMode === "scheduled") {
      const scheduleDate = new Date(activationScheduleInput);
      if (!Number.isFinite(scheduleDate.getTime())) {
        setActivationScheduleError("Selecciona un horario de activación válido.");
        return;
      }
      payload = {
        autoActivationMode: "scheduled",
        autoActivationScheduledAt: scheduleDate.toISOString(),
      };
    }
    const approved = await confirm({
      title: "Finalizar lista",
      message: activationMode === "scheduled"
        ? "Finalizar esta lista? La activación automática ejecutará precios y disponibilidad en el horario programado."
        : "Finalizar esta lista? Se ejecutará la activación ahora para actualizar precios y marcar productos disponibles.",
      confirmLabel: "Finalizar",
      cancelLabel: "Cancelar",
      tone: "primary",
    });
    if (!approved) return;
    setRecommendationActionLoading(true);
    setRecommendationError("");
    try {
      const result = await finalizeStockList(branchId, id, { ...payload, ...(Object.keys(warehousePackQuantities).length ? { warehousePackQuantities } : {}) });
      const { activationStatus } = await applyActivationResponse(result);
      if (activationMode === "scheduled") {
        notify("Lista finalizada. Activación programada.");
      } else if (activationStatus === "failed") {
        notify("Lista finalizada, pero la activación falló.");
      } else {
        notify("Lista finalizada. Precios y disponibilidad actualizados.");
      }
    } catch (error) {
      console.error("Error finalizing stock list:", error);
      setRecommendationError(
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "No se pudo finalizar la lista."
      );
    } finally {
      setRecommendationActionLoading(false);
    }
  };

  const handleRunPriceRecommendations = async () => {
    if (!canUpdateStock || recommendationActionLoading) return;
    if (!isStockListCompleted && pendingFormats.length) { notify('Revisa los formatos pendientes antes de finalizar la lista.'); return; }
    const approved = await confirm({
      title: "Ejecutar recomendaciones",
      message: "Ejecutar ahora las reglas de precios para esta lista? Se usara el modo configurado en Ajustes.",
      confirmLabel: "Ejecutar",
      cancelLabel: "Cancelar",
      tone: "primary",
    });
    if (!approved) return;
    setRecommendationActionLoading(true);
    setRecommendationError("");
    try {
      const result = await runStockListPriceRecommendations(branchId, id);
      const run = result?.recommendationRun || null;
      if (run) {
        setRecommendationRuns((prev) => [
          run,
          ...prev.filter((entry) => Number(entry.id) !== Number(run.id)),
        ]);
      }
      await loadRecommendationRuns();
      notifyRecommendationCompleted(run);
    } catch (error) {
      console.error("Error running price recommendations:", error);
      setRecommendationError(
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "No se pudieron ejecutar las recomendaciones."
      );
    } finally {
      setRecommendationActionLoading(false);
    }
  };

  const handleRunActivationNow = async () => {
    if (!canUpdateStock || recommendationActionLoading) return;
    if (!isStockListCompleted && pendingFormats.length) { notify('Revisa los formatos pendientes antes de finalizar la lista.'); return; }
    const approved = await confirm({
      title: "Ejecutar activación",
      message: "Ejecutar ahora la activación automática? Se actualizarán precios y se marcarán productos disponibles.",
      confirmLabel: "Ejecutar",
      cancelLabel: "Cancelar",
      tone: "primary",
    });
    if (!approved) return;
    setRecommendationActionLoading(true);
    setRecommendationError("");
    try {
      const result = await runStockListActivationNow(branchId, id);
      const { activationStatus } = await applyActivationResponse(result);
      if (activationStatus === "failed") {
        notify("La activación falló.");
      } else {
        notify("Activación completada.");
      }
    } catch (error) {
      console.error("Error running stock list activation:", error);
      setRecommendationError(
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "No se pudo ejecutar la activación."
      );
    } finally {
      setRecommendationActionLoading(false);
    }
  };

  const handleUpdateActivationSchedule = async () => {
    if (!canUpdateStock || activationScheduleSaving) return;
    const scheduleDate = new Date(activationScheduleInput);
    if (!Number.isFinite(scheduleDate.getTime())) {
      setActivationScheduleError("Selecciona un horario de activación válido.");
      return;
    }
    setActivationScheduleSaving(true);
    setActivationScheduleError("");
    try {
      const result = await updateStockListActivationSchedule(branchId, id, {
        autoActivationScheduledAt: scheduleDate.toISOString(),
      });
      if (result?.stockList) {
        setStockList((prev) => ({ ...(prev || {}), ...result.stockList }));
      }
      notify("Activación automática reprogramada.");
    } catch (error) {
      console.error("Error updating stock list activation schedule:", error);
      setActivationScheduleError(
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "No se pudo reprogramar la activación."
      );
    } finally {
      setActivationScheduleSaving(false);
    }
  };

  const handleConfirmSelectedEstimate = async () => {
    if (!canUpdateStock || !selectedItem?.id || !id) return;
    setEstimateActionError("");
    setIsSavingEstimateConfirmation(true);
    try {
      await confirmStockEstimateItem(branchId, id, selectedItem.id);
      setConfirmedEstimateStockIds((prev) => Array.from(new Set([...prev, selectedItem.id])));
    } catch (error) {
      console.error("Error confirming estimate item:", error);
      setEstimateActionError("No se pudo confirmar este item del estimado.");
    } finally {
      setIsSavingEstimateConfirmation(false);
    }
  };

  const handleUnconfirmSelectedEstimate = async () => {
    if (!canUpdateStock || !selectedItem?.id || !id) return;
    setEstimateActionError("");
    setIsSavingEstimateConfirmation(true);
    try {
      await unconfirmStockEstimateItem(branchId, id, selectedItem.id);
      setConfirmedEstimateStockIds((prev) =>
        prev.filter((entryId) => Number(entryId) !== Number(selectedItem.id))
      );
    } catch (error) {
      console.error("Error removing estimate confirmation:", error);
      setEstimateActionError("No se pudo quitar la confirmación del item.");
    } finally {
      setIsSavingEstimateConfirmation(false);
    }
  };

  const handleOpenEquivalenceModal = () => {
    if (!canUpdateStock || !selectedItem?.id) return;
    const options = resolveWholesaleUnitOptions(selectedItem);
    if (!options.length) {
      setEstimateActionError("No hay unidades mayoristas disponibles para crear equivalencia.");
      return;
    }
    setEquivalenceModal({
      isOpen: true,
      stockId: selectedItem.id,
      fromUnit: selectedItem.metricUnit || "",
      toUnit: options[0].label,
      fromAmount: "1",
      toAmount: "",
      error: "",
    });
  };

  const handleCloseEquivalenceModal = () => {
    setEquivalenceModal({
      isOpen: false,
      stockId: null,
      fromUnit: "",
      toUnit: "",
      fromAmount: "1",
      toAmount: "",
      error: "",
    });
  };

  const handleSaveEquivalence = async () => {
    if (!canUpdateStock || !selectedItem?.id) return;
    const fromUnit = String(equivalenceModal.fromUnit || "").trim();
    const toUnit = String(equivalenceModal.toUnit || "").trim();
    const fromAmount = parseDecimalInput(equivalenceModal.fromAmount);
    const toAmount = parseDecimalInput(equivalenceModal.toAmount);
    if (!fromUnit || !toUnit) {
      setEquivalenceModal((prev) => ({ ...prev, error: "Selecciona unidades válidas." }));
      return;
    }
    if (!Number.isFinite(fromAmount) || fromAmount <= 0 || !Number.isFinite(toAmount) || toAmount <= 0) {
      setEquivalenceModal((prev) => ({ ...prev, error: "Ingresa cantidades mayores que 0 para ambas unidades." }));
      return;
    }

    setEstimateActionError("");
    setIsSavingEquivalence(true);
    try {
      await upsertEstimateUnitEquivalence(branchId, { fromUnit, fromAmount, toUnit, toAmount });
      await loadEstimateEquivalences();
      handleCloseEquivalenceModal();
    } catch (error) {
      console.error("Error saving estimate equivalence:", error);
      setEquivalenceModal((prev) => ({
        ...prev,
        error: "No se pudo guardar la equivalencia.",
      }));
    } finally {
      setIsSavingEquivalence(false);
    }
  };

  const handleDeleteEquivalence = async (equivalenceId) => {
    if (!canUpdateStock || !equivalenceId) return;
    setEstimateActionError("");
    setDeletingEquivalenceId(equivalenceId);
    try {
      await deleteEstimateUnitEquivalence(branchId, equivalenceId);
      await loadEstimateEquivalences();
    } catch (error) {
      console.error("Error deleting estimate equivalence:", error);
      setEquivalenceModal((prev) => ({
        ...prev,
        error: "No se pudo eliminar la equivalencia.",
      }));
    } finally {
      setDeletingEquivalenceId(null);
    }
  };

  // Calculate total stock cost and expenses
  const totalStockValue = stockItems.reduce((sum, item) => sum + resolveStockCostAmount(item), 0);
  const totalExpenses = expenses.reduce((sum, exp) => sum + Number(exp.amount || 0), 0);
  const overallTotal = totalStockValue + totalExpenses;
  const importCostSummary = stockList?.importCostSummary || null;
  const importCostTotals = importCostSummary?.totals || {};
  const totalBultos =
    stockList?.stockTotals?.totalBultos ??
    stockItems.reduce((sum, item) => sum + getStockBultos(item), 0);
  const totalKg =
    stockList?.stockTotals?.totalKg ??
    stockItems.reduce((sum, item) => sum + getStockKg(item, { isImportList }), 0);
  const resolveEstimateMetaForItem = (item) => {
    const standardGranel = isStandardGranelPurchase({
      isImportList,
      purchaseMode: item?.purchaseMode,
      metricUnit: item?.metricUnit,
    });
    const quantity = standardGranel
      ? getStandardGranelDisplayWeight(item, { isImportList })
      : Number(item?.quantity || 0);
    return resolveStockWholesaleEstimate({
      stock: item,
      quantity,
      totalWeightKg: getStockKg(item, { isImportList }),
      entries: wholesalePriceMap[item?.varietyId] || [],
      equivalenceMap: estimateEquivalenceMap,
    });
  };
  const estimateMetaByStockId = useMemo(() => {
    const map = new Map();
    (stockItems || []).forEach((item) => {
      map.set(Number(item.id), resolveEstimateMetaForItem(item));
    });
    return map;
  }, [stockItems, wholesalePriceMap, estimateEquivalenceMap, isImportList]);

  const wholesaleEstimate = useMemo(
    () => stockItems.reduce((sum, item) => {
      const estimateMeta = estimateMetaByStockId.get(Number(item.id));
      return sum + (estimateMeta?.considered ? Number(estimateMeta.estimate || 0) : 0);
    }, 0),
    [stockItems, estimateMetaByStockId]
  );

  const missingWholesaleCount = useMemo(
    () => stockItems.filter((item) => !estimateMetaByStockId.get(Number(item.id))?.considered).length,
    [stockItems, estimateMetaByStockId]
  );

  const estimateReviewByStockId = useMemo(() => {
    const map = new Map();
    (stockItems || []).forEach((item) => {
      const stockId = Number(item.id);
      const meta = estimateMetaByStockId.get(stockId);
      if (!meta?.considered || wholesaleEstimate <= 0) {
        map.set(stockId, {
          sharePercent: 0,
          isConfirmed: confirmedEstimateSet.has(stockId),
          needsReview: false,
        });
        return;
      }
      const sharePercent = (Number(meta.estimate || 0) / wholesaleEstimate) * 100;
      const isConfirmed = confirmedEstimateSet.has(stockId);
      const needsReview =
        sharePercent > ESTIMATE_WARNING_THRESHOLD
        && !isConfirmed
        && !meta.usedEquivalence;
      map.set(stockId, { sharePercent, isConfirmed, needsReview });
    });
    return map;
  }, [stockItems, estimateMetaByStockId, wholesaleEstimate, confirmedEstimateSet]);

  const warningEstimateCount = useMemo(
    () => stockItems.filter((item) => estimateReviewByStockId.get(Number(item.id))?.needsReview).length,
    [stockItems, estimateReviewByStockId]
  );

  const importConversionSet = useMemo(() => {
    const set = new Set();
    (importConversions || []).forEach((conversion) => {
      const varietyId = Number(conversion?.varietyId);
      const fromKey = toMetricUnitKey(conversion?.fromUnitKey || conversion?.fromUnit);
      const toKey = toMetricUnitKey(conversion?.toUnitKey || conversion?.toUnit);
      if (!Number.isInteger(varietyId) || !fromKey || !toKey) return;
      set.add(`${varietyId}::${fromKey}::${toKey}`);
    });
    return set;
  }, [importConversions]);

  const resolveMissingImportConversions = useCallback((item) => {
    if (!isImportList || item?.categoryIsResaleOnly || !item?.varietyId || !item?.metricUnit) return [];
    const varietyId = Number(item.varietyId);
    const stockUnitKey = isStockPack(item.metricUnit) && item.unitsPerPack ? 'unidad' : toMetricUnitKey(item.metricUnit);
    if (!Number.isInteger(varietyId) || !stockUnitKey) return [];
    const priceUnits = varietyPriceUnitMap[varietyId] || [];
    return priceUnits.filter((unit) => {
      if (!unit.key || unit.key === stockUnitKey) return false;
      return !importConversionSet.has(`${varietyId}::${stockUnitKey}::${unit.key}`);
    });
  }, [importConversionSet, isImportList, varietyPriceUnitMap]);

  const missingImportConversionCount = useMemo(
    () => stockItems.reduce((sum, item) => sum + resolveMissingImportConversions(item).length, 0),
    [stockItems, resolveMissingImportConversions]
  );

  const hasMissingBultosInfo = stockItems.some((item) => {
    if (isFormatStockPurchase(item)) return false;
    const unit = item?.metricUnit;
    if (!unit) return true;
    if (isBinUnit(unit)) {
      const bultosPerBin = Number(item?.bultosPerBin || 0);
      return !Number.isFinite(bultosPerBin) || bultosPerBin <= 0;
    }
    return false;
  });
  const paymentStatusLabel = {
    unpaid: "Pendiente",
    partial: "Parcial",
    paid: "Pagado",
  };
  const paymentStatusClass = {
    unpaid: "bg-red-100 text-red-600",
    partial: "bg-yellow-100 text-yellow-700",
    paid: "bg-green-100 text-green-600",
  };
  const selectedEditProviderId = String(stockEditForm.providerId || "");
  const normalizedEditProviderSearch = editProviderSearchTerm.trim().toLowerCase();
  const availableEditProviders = useMemo(
    () => editProviders.filter((provider) => !provider.isDeleted),
    [editProviders]
  );
  const filteredEditProviders = useMemo(
    () =>
      availableEditProviders.filter((provider) => {
        const providerId = String(provider.id);
        if (providerId === selectedEditProviderId) return true;
        if (!normalizedEditProviderSearch) return true;
        const name = provider.name?.toLowerCase() || "";
        const rut = provider.rut?.toLowerCase() || "";
        const giro = provider.giro?.toLowerCase() || "";
        return (
          name.includes(normalizedEditProviderSearch) ||
          rut.includes(normalizedEditProviderSearch) ||
          giro.includes(normalizedEditProviderSearch)
        );
      }),
    [availableEditProviders, normalizedEditProviderSearch, selectedEditProviderId]
  );
  const selectedEditProvider = useMemo(() => {
    if (!selectedEditProviderId) return null;
    return (
      editProviders.find((provider) => String(provider.id) === selectedEditProviderId) ||
      (selectedItem?.Provider && String(selectedItem.Provider.id) === selectedEditProviderId
        ? selectedItem.Provider
        : null)
    );
  }, [editProviders, selectedEditProviderId, selectedItem]);
  const selectedIsResaleOnlyImport = Boolean(isImportList && selectedItem?.categoryIsResaleOnly);
  const openStockRegistration = (stock) => setFormatRegistrationStock({ ...stock, registrationScopeKey: scopeKey });
  const stockCatalogContext = (stock) => {
    for (const product of productsCacheRef.current || []) {
      const variety = (product.Varieties || []).find((entry) => String(entry.id) === String(stock?.varietyId));
      if (variety) return { product, variety };
    }
    return { product: {}, variety: {} };
  };
  const selectedPurchaseFormats = stockCatalogContext(selectedItem).variety.purchaseFormats || [];
  const selectedUsesFormatSelector = !isImportList && (stockEditForm.purchaseMode != null || stockEditForm.historicalFormat);
  const selectedIsFormatPurchase = !isImportList && (isEditingStock ? Boolean(stockEditForm.purchaseMode) : Boolean(selectedItem?.purchaseMode));
  const selectedDisplayMetricUnit = isEditingStock
    ? stockEditForm.metricUnit
    : selectedItem?.metricUnit;
  const selectedPurchaseGuidance = getStandardStockPurchaseGuidance({
    isImportList,
    purchaseMode: isEditingStock ? stockEditForm.purchaseMode : selectedItem?.purchaseMode,
    metricUnit: selectedDisplayMetricUnit,
  });
  const selectedStandardGranel = isStandardGranelPurchase({
    isImportList,
    purchaseMode: isEditingStock ? stockEditForm.purchaseMode : selectedItem?.purchaseMode,
    metricUnit: selectedDisplayMetricUnit,
  });
  const selectedPurchaseDisplayForm = createStockEditForm(selectedItem, { isImportList });
  const stockMetricUnitOptions = useMemo(() => {
    if (selectedIsResaleOnlyImport && selectedItem?.varietyId) {
      const saleUnits = varietyPriceUnitMap[Number(selectedItem.varietyId)] || [];
      return [...saleUnits.filter((unit) => !isStockPack(unit.label)).map((unit) => ({ value: unit.label, label: unit.label })), { value: "pack", label: "Pack de X unidades" }];
    }
    return buildMetricUnitOptions(stockEditForm.metricUnit || selectedItem?.metricUnit);
  }, [
    selectedIsResaleOnlyImport,
    selectedItem?.metricUnit,
    selectedItem?.varietyId,
    stockEditForm.metricUnit,
    varietyPriceUnitMap,
  ]);
  useEffect(() => {
    if (!isEditingStock || !selectedIsResaleOnlyImport || stockMetricUnitOptions.length === 0) return;
    const hasSelectedUnit = stockMetricUnitOptions.some((option) => option.value === stockEditForm.metricUnit);
    if (!hasSelectedUnit) {
      handleStockEditMetricUnitChange(stockMetricUnitOptions[0].value);
    }
  }, [
    isEditingStock,
    selectedIsResaleOnlyImport,
    stockEditForm.metricUnit,
    stockMetricUnitOptions,
  ]);
  const previewPrice = Number(stockEditForm.price);
  const previewPriceWithoutIva = Number(stockEditForm.priceWithoutIva || 0);
  const previewPurchaseValues = resolveStockEditPurchaseValues({
    stock: selectedItem,
    form: stockEditForm,
    isImportList,
  });
  const previewQuantity = previewPurchaseValues.quantity;
  const selectedSummary =
    selectedItem && activeTab === "productos"
      ? getPaymentSummary({
          ...selectedItem,
          price:
            isEditingStock && Number.isFinite(previewPrice) && previewPrice >= 0
              ? previewPrice
              : selectedItem.price,
          priceWithoutIva:
            isEditingStock && Number.isFinite(previewPriceWithoutIva) && previewPriceWithoutIva >= 0
              ? previewPriceWithoutIva
              : selectedItem.priceWithoutIva,
          quantity:
            isEditingStock && Number.isFinite(previewQuantity) && previewQuantity > 0
              ? previewQuantity
              : selectedItem.quantity,
        })
      : null;
  const selectedBalance = Number(selectedSummary?.balance || 0);
  const enteredAmount = Number(paymentForm.amount || 0);
  const safeEnteredAmount = Number.isFinite(enteredAmount) ? enteredAmount : 0;
  const remainingAfter = Math.max(selectedBalance - safeEnteredAmount, 0);
  const overpayAmount = safeEnteredAmount > selectedBalance ? safeEnteredAmount - selectedBalance : 0;
  const selectedEstimateMeta =
    selectedItem && activeTab === "productos"
      ? estimateMetaByStockId.get(Number(selectedItem.id))
      : null;
  const selectedEstimateReview =
    selectedItem && activeTab === "productos"
      ? estimateReviewByStockId.get(Number(selectedItem.id))
      : null;
  const selectedEstimateValue =
    selectedEstimateMeta?.considered ? Number(selectedEstimateMeta.estimate || 0) : 0;
  const selectedEstimateShare = Number(selectedEstimateReview?.sharePercent || 0);
  const selectedNeedsEstimateReview = Boolean(selectedEstimateReview?.needsReview);
  const selectedEstimateConfirmed = Boolean(selectedEstimateReview?.isConfirmed);
  const selectedStockEquivalenceOptions = selectedItem
    ? resolveWholesaleUnitOptions(selectedItem)
    : [];
  const selectedMissingImportConversions = selectedItem && activeTab === "productos"
    ? resolveMissingImportConversions(selectedItem)
    : [];
  const selectedSourceUnitKey = toMetricUnitKey(selectedItem?.metricUnit);
  const selectedEquivalencesForSource = selectedSourceUnitKey
    ? (estimateEquivalenceMap.get(selectedSourceUnitKey) || [])
    : [];
  const estimateModeLabel = {
    equivalence: "Equivalencia global",
    exact: "Unidad exacta",
    weight: "Conversión por peso",
  };
  const selectedEstimateSourceLabel = selectedEstimateMeta
    ? (estimateModeLabel[selectedEstimateMeta.mode] || "Estimado")
    : "";
  const modalFromAmountValue = parseDecimalInput(equivalenceModal.fromAmount);
  const modalToAmountValue = parseDecimalInput(equivalenceModal.toAmount);
  const modalHasValidRatio =
    Number.isFinite(modalFromAmountValue) &&
    modalFromAmountValue > 0 &&
    Number.isFinite(modalToAmountValue) &&
    modalToAmountValue > 0;
  const modalForwardRatio = modalHasValidRatio ? (modalToAmountValue / modalFromAmountValue) : null;
  const modalReverseRatio = modalHasValidRatio ? (modalFromAmountValue / modalToAmountValue) : null;
  const modalSourceAmountLabel = modalHasValidRatio ? formatSignificantNumber(modalFromAmountValue) : "?";
  const modalTargetAmountLabel = modalHasValidRatio ? formatSignificantNumber(modalToAmountValue) : "?";
  const modalForwardRatioLabel = modalHasValidRatio ? formatSignificantNumber(modalForwardRatio) : "?";
  const modalReverseRatioLabel = modalHasValidRatio ? formatSignificantNumber(modalReverseRatio) : "?";
  const isStockListCompleted = stockList?.status === "completed";
  const completedAtLabel = stockList?.completedAt
    ? new Date(stockList.completedAt).toLocaleString("es-CL")
    : "";
  const autoActivationStatus = stockList?.autoActivationStatus || "scheduled";
  const autoActivationScheduledDate = stockList?.autoActivationScheduledAt
    ? new Date(stockList.autoActivationScheduledAt)
    : null;
  const autoActivationScheduledLabel =
    autoActivationScheduledDate && Number.isFinite(autoActivationScheduledDate.getTime())
      ? autoActivationScheduledDate.toLocaleString("es-CL")
      : "Sin horario";
  const autoActivationIsDue =
    autoActivationStatus === "scheduled" &&
    autoActivationScheduledDate &&
    Number.isFinite(autoActivationScheduledDate.getTime()) &&
    autoActivationScheduledDate.getTime() <= Date.now();
  const autoActivationDisplayLabel =
    !isStockListCompleted
      ? (activationMode === "scheduled" ? "Programar al finalizar" : "Al finalizar")
      : autoActivationIsDue
        ? "Pendiente"
        : (AUTO_ACTIVATION_STATUS_LABELS[autoActivationStatus] || autoActivationStatus);
  const autoActivationDisplayClass =
    !isStockListCompleted
      ? (activationMode === "scheduled" ? "bg-blue-100 text-blue-700" : "bg-green-100 text-green-700")
      : autoActivationIsDue
        ? "bg-amber-100 text-amber-700"
        : (AUTO_ACTIVATION_STATUS_CLASSES[autoActivationStatus] || "bg-gray-100 text-gray-700");
  const canRescheduleActivation =
    canUpdateStock && isStockListCompleted && ["scheduled", "failed"].includes(autoActivationStatus);
  const canRunActivationNow =
    canUpdateStock && isStockListCompleted && ["scheduled", "failed"].includes(autoActivationStatus);
  const latestRecommendationRun = Array.isArray(recommendationRuns) && recommendationRuns.length
    ? recommendationRuns[0]
    : null;
  const latestRecommendationSummary = latestRecommendationRun?.summary || {};
  const latestRecommendationItems = Array.isArray(latestRecommendationRun?.Items)
    ? latestRecommendationRun.Items
    : [];
  const latestRecommendationProcessLog = getRecommendationRunLog(latestRecommendationRun);
  const reviewableRecommendationItems = latestRecommendationItems.filter((item) =>
    RECOMMENDATION_REVIEW_STATUSES.has(item?.status)
  );
  const recommendationTotalPages = Math.max(
    1,
    Math.ceil(reviewableRecommendationItems.length / RECOMMENDATION_ITEMS_PAGE_SIZE)
  );
  const safeRecommendationPage = Math.min(recommendationPage, recommendationTotalPages);
  const paginatedRecommendationItems = reviewableRecommendationItems.slice(
    (safeRecommendationPage - 1) * RECOMMENDATION_ITEMS_PAGE_SIZE,
    safeRecommendationPage * RECOMMENDATION_ITEMS_PAGE_SIZE
  );
  const recommendationPageStart = reviewableRecommendationItems.length
    ? (safeRecommendationPage - 1) * RECOMMENDATION_ITEMS_PAGE_SIZE + 1
    : 0;
  const recommendationPageEnd = Math.min(
    safeRecommendationPage * RECOMMENDATION_ITEMS_PAGE_SIZE,
    reviewableRecommendationItems.length
  );

  useEffect(() => {
    setRecommendationPage(1);
    setExpandedRecommendationItemId(null);
  }, [latestRecommendationRun?.id]);

  useEffect(() => {
    if (recommendationPage <= recommendationTotalPages) return;
    setRecommendationPage(recommendationTotalPages);
  }, [recommendationPage, recommendationTotalPages]);

  if (loading) {
    return (
      <p className="text-center mt-6">
        <InlineLoading />
      </p>
    );
  }

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      {formatRegistrationStock?.registrationScopeKey === scopeKey && ['pending', 'changes_requested'].includes(formatRegistrationStock.formatReviewStatus) && canManageFormats && <StockFormatReviewDialog
        key={`review-${formatRegistrationStock.id}`} stock={formatRegistrationStock} {...stockCatalogContext(formatRegistrationStock)} branchId={branchId}
        onClose={() => setFormatRegistrationStock(null)} onSaved={async () => {
          if (activeScopeRef.current !== formatRegistrationStock.registrationScopeKey) return;
          await refreshStockListItems(selectedItem?.id); await fetchWholesalePrices();
        }} /> }
      {formatRegistrationStock?.registrationScopeKey === scopeKey && !['pending', 'changes_requested'].includes(formatRegistrationStock.formatReviewStatus) && canManageFormats && <StockFormatRegistrationDialog
        key={formatRegistrationStock.id} stock={formatRegistrationStock}
        {...stockCatalogContext(formatRegistrationStock)} branchId={branchId}
        canUpdateProduct={canUpdateStock && canUpdateProduct} canUpdatePrice={canUpdateStock && canUpdatePrice}
        onClose={() => setFormatRegistrationStock(null)}
        onSaved={async (result) => {
          if (activeScopeRef.current !== formatRegistrationStock.registrationScopeKey) return;
          await refreshStockListItems(selectedItem?.id);
          await fetchWholesalePrices();
          notify(result?.reusedExistingFormat ? 'Formato existente vinculado. Se conservaron sus datos configurados.' : 'Formato guardado.');
        }}
      />}
      {expenseTarget?.scopeKey === scopeKey && <StockExpenseDialog
        key={`${scopeKey}:${expenseTarget.expense.id}`}
        expense={expenseTarget.expense} initialMode={expenseTarget.mode}
        branchId={branchId} stockListId={id} isImportList={isImportList}
        canUpdate={canUpdateStock} canDelete={canDeleteStock}
        onClose={() => setExpenseTarget(null)}
        onChanged={({ action, id: expenseId, expense }) => {
          if (activeScopeRef.current !== expenseTarget.scopeKey) return;
          setExpenses((current) => action === 'deleted'
            ? current.filter((entry) => entry.id !== expenseId)
            : current.map((entry) => entry.id === expenseId ? { ...entry, ...expense } : entry));
          setExpenseTarget(null);
          notify(action === 'deleted' ? 'Gasto eliminado.' : 'Gasto actualizado.');
          void refreshStockListItems().catch(() => {
            if (activeScopeRef.current === expenseTarget.scopeKey) notify('El gasto se guardó. Recarga la entrada para actualizar su detalle.');
          });
        }}
      />}
      <div className="max-w-7xl mx-auto">
        {/* Page Title */}
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
          <div className="md:flex-1 text-center md:text-left">
            <h1 className="text-2xl font-bold">{stockList.name}</h1>
            {stockList.warehouseDispatchId && <p className="mt-3 rounded-lg bg-blue-50 p-3 text-sm text-blue-900">Entrada vinculada a un traslado. <a className="font-semibold underline" href={`/branches/${branchId}/delivery-guides/${stockList.warehouseDispatchId}/details`}>Ver guía y verificar recepción</a></p>}
            {pendingFormats.length > 0 && <section aria-label="Formatos pendientes" className="mt-3 rounded-lg bg-amber-50 p-3 text-left text-sm text-amber-900">
              <p className="font-semibold">{pendingFormats.length} formatos pendientes · Finalización bloqueada</p>
              {pendingFormats.map((stock) => <div key={stock.id} className="mt-2 flex items-center justify-between gap-3">
                <span>{stock.varietyName} · {(stock.sellingSnapshot || stock.purchaseSnapshot)?.metricUnit || stock.metricUnit}</span>
                {canManageFormats && <button type="button" className="font-semibold underline" onClick={() => openStockRegistration(stock)}>Revisar</button>}
              </div>)}
            </section>}
            <div className="mt-2 flex flex-wrap justify-center md:justify-start gap-2 text-xs">
              <span
                className={`px-2 py-1 rounded-full font-semibold ${
                  isStockListCompleted
                    ? "bg-green-100 text-green-700"
                    : "bg-gray-200 text-gray-700"
                }`}
              >
                {isStockListCompleted ? "Completada" : "Borrador"}
              </span>
              {completedAtLabel && (
                <span className="px-2 py-1 rounded-full bg-white border border-gray-200 text-gray-600">
                  {completedAtLabel}
                </span>
              )}
            </div>
          </div>
          {(canUpdateStock || hasPermission(user, 'stock.read')) && (
            <div className="flex flex-col sm:flex-row gap-2 justify-center md:justify-end">
              {isStockListCompleted && String(stockList.id) === String(id) && hasPermission(user, 'stock.read') && (
                <StockAccountingEmailAction key={scopeKey} branchId={branchId} stockList={stockList} />
              )}
              {canUpdateStock && !isStockListCompleted && !stockList.warehouseDispatchId && (
                <button
                  type="button"
                  className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm hover:bg-blue-700 disabled:opacity-60"
                  onClick={handleFinalizeStockList}
                  disabled={recommendationActionLoading}
                >
                  {recommendationActionLoading ? "Procesando..." : "Finalizar lista"}
                </button>
              )}
            </div>
          )}
        </div>

        {!isStockListCompleted && stockItems.some(row => row.purchaseMode === 'granel' && row.Variety?.Product?.Category?.inventoryTrackingMode === 'pack_warehouse') && <fieldset className="my-4 rounded-xl border border-blue-200 bg-blue-50 p-4 space-y-3">
          <legend className="font-semibold">Packs preparados al recibir granel</legend>
          <p className="text-sm text-slate-600">Para categorías con bodega por packs, indica el conteo real del formato seleccionado. Se conservan los kg y costos de la compra.</p>
          {stockItems.filter(row => row.purchaseMode === 'granel' && row.Variety?.Product?.Category?.inventoryTrackingMode === 'pack_warehouse').map(row => <label className="block text-sm" key={row.id}>{row.varietyName} · {row.sellingSnapshot?.metricUnit || 'Pack seleccionado'}
            <input aria-label={`Packs preparados ${row.varietyName}`} type="number" min="0" step="1" inputMode="numeric" className="mt-1 w-full rounded-lg border p-3"
              value={warehousePackQuantities[row.id] ?? row.warehousePackQuantity ?? ''} disabled={!canUpdateStock || recommendationActionLoading}
              onChange={event => setWarehousePackQuantities(current => ({ ...current, [row.id]: event.target.value }))} />
          </label>)}
        </fieldset>}
        {/* Total Info */}
        <div className="text-center text-lg font-semibold text-gray-700 mt-2">
          <p className="text-blue-600 text-xl">Total General: ${formatPesoAmount(overallTotal)}</p>
        </div>
        <div className="text-center text-sm text-emerald-700 mt-1 flex items-center justify-center gap-2">
          <span>
            {loadingWholesale
              ? "Calculando estimado mayorista..."
              : `Estimado venta mayorista: $${formatPesoAmount(wholesaleEstimate)}`}
          </span>
          {!loadingWholesale && (
          <span
              className="inline-flex items-center text-emerald-700"
              title="La cantidad comprada se convierte al formato de venta usando su peso o una equivalencia válida. Se respeta el formato seleccionado; si no hay selección, se usa el menor estimado mayorista compatible."
            >
              <FiInfo className="h-4 w-4" aria-hidden="true" />
            </span>
          )}
        </div>
        {(loadingEstimateEquivalences || loadingEstimateConfirmations) && (
          <p className="text-xs text-gray-500 mt-1 text-center">
            Cargando revisiones del estimado...
          </p>
        )}
        {wholesaleError && (
          <p className="text-xs text-red-600 mt-1 text-center">{wholesaleError}</p>
        )}
        {estimateReviewError && (
          <p className="text-xs text-red-600 mt-1 text-center">{estimateReviewError}</p>
        )}
        {estimateActionError && (
          <p className="text-xs text-red-600 mt-1 text-center">{estimateActionError}</p>
        )}
        {!loadingWholesale && warningEstimateCount > 0 && (
          <p className="text-xs text-amber-700 mt-1 text-center font-semibold">
            {warningEstimateCount} items superan {ESTIMATE_WARNING_THRESHOLD}% del estimado y requieren confirmación o equivalencia.
          </p>
        )}
        {!loadingWholesale && !wholesaleError && missingWholesaleCount > 0 && (
          <p className="text-xs text-gray-500 mt-1 text-center">
            {missingWholesaleCount} items sin precio mayorista considerado.
          </p>
        )}
        <div className="flex flex-wrap justify-center gap-3 mt-3 text-sm text-gray-700">
          <span className="bg-white px-3 py-1 rounded shadow-sm">
            Bultos: {Number(totalBultos || 0).toLocaleString("de-DE")}
            {hasMissingBultosInfo && (
              <span
                className="inline-flex items-center ml-2 text-gray-500"
                title="La cantidad de bultos no esta informada en todos los productos, por lo que el valor es un estimado."
              >
                <FiInfo className="h-4 w-4" aria-hidden="true" />
              </span>
            )}
          </span>
          <span className="bg-white px-3 py-1 rounded shadow-sm">
            Kg: {formatKg(totalKg)}
          </span>
        </div>
        <p className="text-xs text-gray-500 mt-2 text-center">
          {isImportList
            ? "Bultos: suma de cantidades (Bin usa bultos por bin; granel no suma). Kg: cantidad * peso; Bin/Granel usan la cantidad."
            : "Bultos: suma de cantidades (Bin usa bultos por bin; granel no suma). Kg: cantidad × peso; Bin usa cantidad × peso bruto por bin (o cantidad si no hay peso) y granel usa la cantidad."}
        </p>
        {isImportList && (
          <div className="mt-4 bg-white rounded-lg border border-indigo-200 shadow-sm p-4">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
              <div>
                <h2 className="text-sm font-semibold text-indigo-900">Importaciones</h2>
                <p className="text-xs text-indigo-700">
                  Costos prorrateados por volumen, peso o precio segun cada gasto.
                </p>
              </div>
              <span className="w-fit rounded-full bg-indigo-100 px-2 py-1 text-xs font-semibold text-indigo-700">
                Lista de importación
              </span>
            </div>
            <div className="mt-3 grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
              <div className="rounded-lg bg-indigo-50 px-3 py-2">
                <p className="text-indigo-700">Volumen</p>
                <p className="font-semibold text-indigo-950">
                  {Number(importCostTotals.totalVolumeCm3 || 0).toLocaleString("de-DE")} cm3
                </p>
              </div>
              <div className="rounded-lg bg-indigo-50 px-3 py-2">
                <p className="text-indigo-700">Peso import.</p>
                <p className="font-semibold text-indigo-950">{formatKg(importCostTotals.totalWeightKg || 0)} kg</p>
              </div>
              <div className="rounded-lg bg-indigo-50 px-3 py-2">
                <p className="text-indigo-700">Subtotal proveedor</p>
                <p className="font-semibold text-indigo-950">
                  ${formatPesoAmount(Number(importCostTotals.totalProviderSubtotal || 0))}
                </p>
              </div>
              <div className="rounded-lg bg-indigo-50 px-3 py-2">
                <p className="text-indigo-700">Gastos asignados</p>
                <p className="font-semibold text-indigo-950">
                  ${formatPesoAmount(Number(importCostTotals.totalAllocatedExpenses || 0))}
                </p>
              </div>
              <div className="rounded-lg bg-indigo-50 px-3 py-2">
                <p className="text-indigo-700">Costo landed</p>
                <p className="font-semibold text-indigo-950">
                  ${formatPesoAmount(Number(importCostTotals.totalReferenceCost || 0))}
                </p>
              </div>
            </div>
            {importCostSummary?.errors?.length > 0 && (
              <p className="mt-2 text-xs font-semibold text-red-600">
                {importCostSummary.errors.length} datos de importación pendientes antes de finalizar.
              </p>
            )}
            {missingImportConversionCount > 0 && (
              <p className="mt-2 text-xs font-semibold text-amber-700">
                {missingImportConversionCount} conversiones de unidad pendientes para recomendaciones.
              </p>
            )}
            {importConversionsError && (
              <p className="mt-2 text-xs text-red-600">{importConversionsError}</p>
            )}
          </div>
        )}
        <div className="mt-4 bg-white rounded-lg border border-gray-200 shadow-sm p-4">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
            <div>
              <h2 className="text-sm font-semibold text-gray-800">Activación automática</h2>
              <p className="mt-1 text-xs text-gray-500">
                {!isStockListCompleted
                  ? activationMode === "scheduled"
                    ? `Se ejecutará al finalizar, programada para ${autoActivationScheduledLabel}`
                    : "Se ejecutará al finalizar para actualizar precios y disponibilidad."
                  : autoActivationStatus === "completed"
                  ? `Completada${stockList.autoActivationCompletedAt ? `: ${new Date(stockList.autoActivationCompletedAt).toLocaleString("es-CL")}` : ""}`
                  : `Programada para ${autoActivationScheduledLabel}`}
              </p>
              {autoActivationStatus === "failed" && stockList.autoActivationError && (
                <p className="mt-2 text-xs text-red-600">{stockList.autoActivationError}</p>
              )}
              {autoActivationIsDue && !isStockListCompleted && (
                <p className="mt-2 text-xs text-amber-700">
                  El horario ya pasó; se ejecutará cuando la lista sea finalizada.
                </p>
              )}
            </div>
            <span className={`w-fit px-2 py-1 rounded-full text-xs font-semibold ${autoActivationDisplayClass}`}>
              {autoActivationDisplayLabel}
            </span>
          </div>
          {canUpdateStock && !isStockListCompleted && (
            <div className="mt-3 space-y-3">
              <div className="grid grid-cols-2 gap-2 max-w-md">
                {[
                  { value: "now", label: "Ahora" },
                  { value: "scheduled", label: "Programar" },
                ].map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => {
                      setActivationMode(option.value);
                      if (option.value === "scheduled" && !activationScheduleInput) {
                        setActivationScheduleInput(getDefaultActivationInput());
                      }
                    }}
                    className={`rounded-lg border px-3 py-2 text-sm font-semibold ${
                      activationMode === option.value
                        ? "border-slate-900 bg-slate-900 text-white"
                        : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
              {activationMode === "scheduled" && (
                <div className="max-w-md">
                  <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
                    Horario
                  </label>
                  <input
                    type="datetime-local"
                    value={activationScheduleInput}
                    onChange={(event) => setActivationScheduleInput(event.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  />
                </div>
              )}
            </div>
          )}
          {canRescheduleActivation && (
            <div className="mt-3 grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_auto] gap-2 md:items-end">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
                  Horario
                </label>
                <input
                  type="datetime-local"
                  value={activationScheduleInput}
                  onChange={(event) => setActivationScheduleInput(event.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                />
              </div>
              <button
                type="button"
                onClick={handleUpdateActivationSchedule}
                disabled={activationScheduleSaving}
                className="px-4 py-2 rounded-lg bg-slate-800 text-white text-sm hover:bg-slate-900 disabled:opacity-60"
              >
                {activationScheduleSaving ? "Guardando..." : "Reprogramar"}
              </button>
            </div>
          )}
          {canRunActivationNow && (
            <button
              type="button"
              onClick={handleRunActivationNow}
              disabled={recommendationActionLoading}
              className="mt-3 px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm hover:bg-emerald-700 disabled:opacity-60"
            >
              {recommendationActionLoading ? "Procesando..." : "Ejecutar activación ahora"}
            </button>
          )}
          {activationScheduleError && (
            <p className="mt-2 text-xs text-red-600">{activationScheduleError}</p>
          )}
        </div>
        <div className="mt-4 bg-white rounded-lg border border-gray-200 shadow-sm p-4">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
            <div>
              <h2 className="text-sm font-semibold text-gray-800">Recomendaciones de precios</h2>
              <p className="text-xs text-gray-500">
                {recommendationLoading
                  ? "Cargando ejecuciones..."
                  : latestRecommendationRun
                    ? `Ultima ejecucion: ${formatDateTimeDisplay(latestRecommendationRun.createdAt || latestRecommendationRun.completedAt)}`
                    : "Sin ejecuciones registradas para esta lista."}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {latestRecommendationRun && (
                <>
                  <span className={`w-fit px-2 py-1 rounded-full text-xs font-semibold ${RECOMMENDATION_RUN_STATUS_CLASSES[latestRecommendationRun.status] || "bg-gray-100 text-gray-700"}`}>
                    {RECOMMENDATION_RUN_STATUS_LABELS[latestRecommendationRun.status] || latestRecommendationRun.status}
                  </span>
                  <span className={`w-fit px-2 py-1 rounded-full text-xs font-semibold ${latestRecommendationRun.autoUpdatePrices === false ? "bg-blue-100 text-blue-700" : "bg-green-100 text-green-700"}`}>
                    {getRecommendationRunModeLabel(latestRecommendationRun)}
                  </span>
                </>
              )}
              {canUpdateStock && (
                <button
                  type="button"
                  className="px-3 py-1.5 rounded-lg bg-slate-800 text-white text-xs font-semibold hover:bg-slate-900 disabled:opacity-60"
                  onClick={handleRunPriceRecommendations}
                  disabled={recommendationActionLoading || recommendationLoading}
                >
                  {recommendationActionLoading ? "Procesando..." : "Recalcular precios"}
                </button>
              )}
            </div>
          </div>
          {recommendationError && (
            <p className="mt-2 text-xs text-red-600">{recommendationError}</p>
          )}
          {latestRecommendationRun && (
            <>
              <div className="mt-3 grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
                <div className="rounded-lg bg-gray-50 px-3 py-2">
                  <p className="text-gray-500">Total</p>
                  <p className="font-semibold text-gray-900">{latestRecommendationSummary.total || 0}</p>
                </div>
                <div className="rounded-lg bg-green-50 px-3 py-2">
                  <p className="text-green-700">Aplicados</p>
                  <p className="font-semibold text-green-900">{latestRecommendationSummary.applied || 0}</p>
                </div>
                <div className="rounded-lg bg-blue-50 px-3 py-2">
                  <p className="text-blue-700">Recomendados</p>
                  <p className="font-semibold text-blue-900">{latestRecommendationSummary.recommended || 0}</p>
                </div>
                <div className="rounded-lg bg-gray-50 px-3 py-2">
                  <p className="text-gray-500">Sin cambio</p>
                  <p className="font-semibold text-gray-900">{latestRecommendationSummary.unchanged || 0}</p>
                </div>
                <div className="rounded-lg bg-amber-50 px-3 py-2">
                  <p className="text-amber-700">Omitidos</p>
                  <p className="font-semibold text-amber-900">{latestRecommendationSummary.skipped || 0}</p>
                </div>
              </div>
              {latestRecommendationProcessLog.length > 0 && (
                <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Registro</p>
                  <ol className="mt-2 space-y-1 text-xs text-gray-700">
                    {latestRecommendationProcessLog.slice(-5).map((entry, index) => (
                      <li key={`${entry.at || "log"}-${index}`} className="flex flex-col gap-0.5 sm:flex-row sm:items-center sm:gap-2">
                        <span className="font-medium text-gray-500">{formatDateTimeDisplay(entry.at)}</span>
                        <span>{entry.message || entry.stage || "Actualizacion de estado"}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              )}
              {reviewableRecommendationItems.length > 0 ? (
                <div className="mt-3">
                  <div className="mb-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <p className="text-xs text-gray-500">
                      Mostrando {recommendationPageStart}-{recommendationPageEnd} de {reviewableRecommendationItems.length} precios aplicados o recomendados.
                    </p>
                    {recommendationTotalPages > 1 && (
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setRecommendationPage((page) => Math.max(1, page - 1))}
                          disabled={safeRecommendationPage <= 1}
                          className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-40"
                          aria-label="Pagina anterior"
                          title="Pagina anterior"
                        >
                          <FiChevronLeft aria-hidden="true" />
                        </button>
                        <span className="text-xs font-semibold text-gray-600">
                          {safeRecommendationPage} / {recommendationTotalPages}
                        </span>
                        <button
                          type="button"
                          onClick={() => setRecommendationPage((page) => Math.min(recommendationTotalPages, page + 1))}
                          disabled={safeRecommendationPage >= recommendationTotalPages}
                          className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-40"
                          aria-label="Pagina siguiente"
                          title="Pagina siguiente"
                        >
                          <FiChevronRight aria-hidden="true" />
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-xs">
                      <thead className="bg-gray-50 text-gray-600">
                        <tr>
                          <th className="px-2 py-2 text-left font-semibold">Producto</th>
                          <th className="px-2 py-2 text-left font-semibold">Variedad</th>
                          <th className="px-2 py-2 text-left font-semibold">Precio variedad</th>
                          <th className="px-2 py-2 text-left font-semibold">Cliente</th>
                          <th className="px-2 py-2 text-right font-semibold">Anterior</th>
                          <th className="px-2 py-2 text-right font-semibold">Recomendado</th>
                          <th className="px-2 py-2 text-left font-semibold">Regla</th>
                          <th className="px-2 py-2 text-left font-semibold">Estado</th>
                          <th className="px-2 py-2 text-center font-semibold">Detalle</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {paginatedRecommendationItems.map((item) => {
                          const isExpanded = Number(expandedRecommendationItemId) === Number(item.id);
                          const detailRows = getRecommendationCalculationDetails(item, latestRecommendationRun);
                          const referenceCostDetails = getRecommendationReferenceCostDetails(item);
                          return (
                            <React.Fragment key={item.id}>
                              <tr className={item.warning === "price_below_reference_cost" ? "bg-amber-50" : ""}>
                                <td className="px-2 py-2 text-gray-700">
                                  <div className="flex min-w-[12rem] items-center gap-2">
                                    <img
                                      src={getRecommendationItemImage(item)}
                                      alt={getRecommendationProductName(item)}
                                      className="h-10 w-10 rounded-lg object-cover bg-gray-100 flex-none"
                                    />
                                    <div className="min-w-0">
                                      <p className="font-semibold text-gray-900 break-words">
                                        {getRecommendationProductName(item)}
                                      </p>
                                      {item.VarietyPrice?.BranchVarietyPrices?.[0]?.isSoldOut && (
                                        <p className="text-[11px] text-amber-700">Marcado sin stock</p>
                                      )}
                                    </div>
                                  </div>
                                </td>
                                <td className="px-2 py-2 text-gray-700">
                                  {getRecommendationVarietyName(item)}
                                </td>
                                <td className="px-2 py-2 text-gray-700">
                                  <div className="min-w-[10rem]">
                                    <p className="font-semibold text-gray-900">
                                      {getRecommendationVarietyPriceLabel(item)}
                                    </p>
                                    <p className="text-[11px] text-gray-500">
                                      ID {item.varietyPriceId || "-"}
                                    </p>
                                  </div>
                                </td>
                                <td className="px-2 py-2 text-gray-600">
                                  {CLIENT_TYPE_LABELS[item.clientType] || item.clientType || "-"}
                                </td>
                                <td className="px-2 py-2 text-right text-gray-700">
                                  {formatRecommendationMoney(item.oldPrice, 0)}
                                </td>
                                <td className="px-2 py-2 text-right font-semibold text-gray-900">
                                  {formatRecommendationMoney(item.recommendedPrice, 0)}
                                </td>
                                <td className="px-2 py-2 text-gray-700">
                                  {getRecommendationRuleLabel(item)}
                                </td>
                                <td className="px-2 py-2">
                                  <span className={`px-2 py-1 rounded-full font-semibold ${RECOMMENDATION_STATUS_CLASSES[item.status] || "bg-gray-100 text-gray-600"}`}>
                                    {RECOMMENDATION_STATUS_LABELS[item.status] || item.status}
                                  </span>
                                  {item.warning === "price_below_reference_cost" && (
                                    <span className="ml-2 px-2 py-1 rounded-full bg-amber-100 text-amber-800 font-semibold">
                                      Bajo costo ref.
                                    </span>
                                  )}
                                </td>
                                <td className="px-2 py-2 text-center">
                                  <button
                                    type="button"
                                    onClick={() => setExpandedRecommendationItemId((currentId) =>
                                      Number(currentId) === Number(item.id) ? null : item.id
                                    )}
                                    className={`inline-flex h-8 w-8 items-center justify-center rounded-lg border text-gray-600 hover:bg-gray-50 ${
                                      isExpanded ? "border-blue-300 bg-blue-50 text-blue-700" : "border-gray-200 bg-white"
                                    }`}
                                    aria-label="Ver detalle del calculo"
                                    title="Ver detalle del calculo"
                                    aria-expanded={isExpanded}
                                  >
                                    <FiEye aria-hidden="true" />
                                  </button>
                                </td>
                              </tr>
                              {isExpanded && (
                                <tr className="bg-slate-50">
                                  <td className="px-3 py-3" colSpan={9}>
                                    <div className="grid gap-2 md:grid-cols-3">
                                      {detailRows.map((detail) => (
                                        <div key={detail.label} className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                                          <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                                            {detail.label}
                                          </p>
                                          <p className="mt-1 break-words font-semibold text-slate-900">
                                            {detail.value}
                                          </p>
                                        </div>
                                      ))}
                                    </div>
                                    <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-3">
                                      <h4 className="text-xs font-semibold uppercase tracking-wide text-emerald-800">
                                        Costo estimado / referencia
                                      </h4>
                                      <div className="mt-2 grid gap-2 md:grid-cols-3">
                                        {referenceCostDetails.map((detail) => (
                                          <div key={detail.label} className="rounded-lg border border-emerald-100 bg-white px-3 py-2">
                                            <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700">
                                              {detail.label}
                                            </p>
                                            <p className="mt-1 break-words font-semibold text-slate-900">
                                              {detail.value}
                                            </p>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : latestRecommendationItems.length > 0 && (
                <p className="mt-3 text-xs text-gray-500">
                  Esta ejecución no tuvo precios aplicados ni recomendados para revisar.
                </p>
              )}
            </>
          )}
        </div>

        {/* Tabs */}
        <div className="flex justify-center gap-4 my-6">
          <button
            className={`px-5 py-3 rounded-lg font-semibold ${activeTab === "productos" ? "bg-blue-500 text-white" : "bg-white shadow"
              }`}
            onClick={() => setActiveTab("productos")}
          >
            Productos
          </button>
          <button
            className={`px-5 py-3 rounded-lg font-semibold ${activeTab === "gastos" ? "bg-blue-500 text-white" : "bg-white shadow"
              }`}
            onClick={() => setActiveTab("gastos")}
          >
            Gastos
          </button>
        </div>

        {/* Create New Item Buttons */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold">
            {activeTab === "productos" ? "Productos" : "Gastos"}
          </h2>
          {canCreateStock && (
            <button
              onClick={() => navigate(`/branches/${branchId}/stock/${id}/${activeTab === "productos" ? "create" : "create-expense"}`)}
              className="bg-green-500 text-white px-5 py-3 rounded hover:bg-green-600"
            >
              {activeTab === "productos" ? "Añadir Producto" : "Añadir Gasto"}
            </button>
          )}
        </div>
        {activeTab === "productos" ? <p className="font-bold text-center w-full py-4">Costo Productos: ${formatPesoAmount(totalStockValue)}</p> : <p className="font-bold text-center w-full py-4">Total Gastos: ${formatPesoAmount(totalExpenses)}</p>}

        {/* Products Tab */}
        {activeTab === "productos" && (
          <div className="bg-white rounded-lg shadow">
            {stockItems.length === 0 ? (
              <p className="text-center text-gray-500 py-6">No hay productos en esta lista.</p>
            ) : (
              <>
                <div className="md:hidden p-3 space-y-3">
                  {stockItems.map((stock) => {
                    const summary = getPaymentSummary(stock);
                    const status = stock.paymentStatus || summary.paymentStatus;
                    const paidAmountValue = stock.paidAmount ?? summary.paidAmount;
                    const balanceValue = stock.balance ?? summary.balance;
                    const stockAmounts = calculateStockAmounts(stock);
                    const standardGranel = isStandardGranelPurchase({
                      isImportList,
                      purchaseMode: stock.purchaseMode,
                      metricUnit: stock.metricUnit,
                    });
                    const purchaseDisplay = standardGranel
                      ? createStockEditForm(stock, { isImportList })
                      : null;
                    const unitPrice = Number(purchaseDisplay?.price ?? stock.price ?? 0);
                    const unitPriceWithoutIva = Number(
                      purchaseDisplay?.priceWithoutIva ?? stock.priceWithoutIva ?? 0
                    );
                    const qty = standardGranel
                      ? getStandardGranelDisplayWeight(stock, { isImportList })
                      : Number(stock.quantity || 0);
                    const subtotal = summary.stockCostAmount;
                    const estimateReview = estimateReviewByStockId.get(Number(stock.id));
                    const missingConversions = resolveMissingImportConversions(stock);

                    return (
                      <article key={stock.id} className="p-4 space-y-3 rounded-xl border border-gray-200 shadow-sm">
                        <button
                          type="button"
                          className="w-full text-left"
                          onClick={() => setSelectedItem(stock)}
                        >
                          <div className="flex items-start gap-3">
                            <img
                              src={stock.varietyImage || "https://placehold.co/200"}
                              alt={stock.varietyName || "Producto sin nombre"}
                              className="w-14 h-14 object-cover rounded-lg flex-none"
                            />
                            <div className="min-w-0">
                              <p className="font-semibold text-gray-900 break-words">
                                {stock.varietyName || "Producto"}
                              </p>
                              {stock.Provider?.name && (
                                <p className="text-xs text-gray-500 break-words">
                                  Proveedor: {stock.Provider.name}
                                </p>
                              )}
                              {estimateReview?.needsReview && (
                                <span className="mt-1 inline-flex px-2 py-0.5 text-[10px] font-semibold rounded-full bg-amber-100 text-amber-700">
                                  Revisar estimado
                                </span>
                              )}
                            </div>
                          </div>
                        </button>
                        <StockFormatRegistrationNotice stock={stock} listStatus={stockList?.status} onRegister={openStockRegistration} canManageFormats={canManageFormats} />

                        <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
                          <div className="rounded-lg bg-gray-50 px-2 py-1.5">
                            <p className="text-[11px] text-gray-500">Formato de compra</p>
                            <p className="font-semibold text-gray-800">{stockPackLabel(stock) || "unidad"}</p>
                          </div>
                          <div className="rounded-lg bg-gray-50 px-2 py-1.5">
                            <p className="text-[11px] text-gray-500">
                              {standardGranel ? "Peso total comprado" : "Cantidad comprada"}
                            </p>
                            <p className="font-semibold text-gray-800">
                              {formatSignificantNumber(qty)}{standardGranel ? " kg" : ""}
                            </p>
                          </div>
                          <div className="col-span-2 rounded-lg bg-emerald-50 px-2 py-1.5">
                            <StockSellingEstimateSummary meta={estimateMetaByStockId.get(Number(stock.id))} loading={loadingWholesale} />
                          </div>
                          {isImportList && (
                            <>
                              <div className="rounded-lg bg-indigo-50 px-2 py-1.5">
                                <p className="text-[11px] text-indigo-700">Peso import.</p>
                                <p className="font-semibold text-indigo-950">
                                  {formatKg(Number(stock.weightKg ?? stock.weight ?? 0))}
                                </p>
                              </div>
                              <div className="rounded-lg bg-indigo-50 px-2 py-1.5">
                                <p className="text-[11px] text-indigo-700">Dimensiones</p>
                                <p className="font-semibold text-indigo-950">
                                  {stock.lengthCm && stock.widthCm && stock.heightCm
                                    ? `${formatSignificantNumber(stock.lengthCm)} x ${formatSignificantNumber(stock.widthCm)} x ${formatSignificantNumber(stock.heightCm)} cm`
                                    : "Pendiente"}
                                </p>
                              </div>
                            </>
                          )}
                          <div className="rounded-lg bg-gray-50 px-2 py-1.5">
                            <p className="text-[11px] text-gray-500">Monto comprado con IVA</p>
                            <p className="font-semibold text-gray-800">
                              ${formatPesoAmount(unitPrice)}
                            </p>
                            {unitPriceWithoutIva > 0 && (
                              <p className="text-[11px] text-gray-500">
                                Monto comprado sin IVA: ${formatPesoAmount(unitPriceWithoutIva)}
                              </p>
                            )}
                          </div>
                          <div className="rounded-lg bg-gray-50 px-2 py-1.5">
                            <p className="text-[11px] text-gray-500">Costo stock</p>
                            <p className="font-semibold text-gray-900">
                              ${formatPesoAmount(subtotal)}
                            </p>
                            {stockAmounts.ivaCostUpliftAmount > 0 && (
                              <p className="text-[11px] text-gray-500">
                                IVA no adeudado: ${formatPesoAmount(stockAmounts.ivaCostUpliftAmount)}
                              </p>
                            )}
                          </div>
                        </div>

                        <div className="flex items-start justify-between gap-3">
                          <div className="text-xs text-gray-600">
                            <p>Pagado: ${formatPesoAmount(Number(paidAmountValue || 0))}</p>
                            <p>Pendiente: ${formatPesoAmount(Number(balanceValue || 0))}</p>
                            {isImportList && missingConversions.length > 0 && (
                              <p className="font-semibold text-amber-700">
                                Conversiones: {missingConversions.map((unit) => unit.label).join(", ")}
                              </p>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            {status && (
                              <span
                                className={`inline-flex px-2 py-1 text-xs font-semibold rounded ${paymentStatusClass[status] || "bg-gray-100 text-gray-600"}`}
                              >
                                {paymentStatusLabel[status] || "Pendiente"}
                              </span>
                            )}
                            {canDeleteStock && (
                              <button
                                type="button"
                                onClick={(event) => handleDeleteStockFromList(event, stock)}
                                disabled={deletingStockId === stock.id}
                                className="bg-red-500 text-white p-2 rounded hover:bg-red-600 disabled:opacity-60"
                                aria-label="Borrar compra"
                                title="Borrar compra"
                              >
                                {deletingStockId === stock.id ? (
                                  <span className="text-xs">...</span>
                                ) : (
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    strokeWidth="1.5"
                                    stroke="currentColor"
                                    className="w-4 h-4"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.166L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.021-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
                                    />
                                  </svg>
                                )}
                              </button>
                            )}
                          </div>
                        </div>
                      </article>
                    );
                  })}
                </div>

                <div className="hidden md:block overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 text-gray-600">
                      <tr>
                        <th className="px-3 py-2 text-left font-semibold">Producto</th>
                        <th className="px-3 py-2 text-left font-semibold">Compra / venta</th>
                        <th className="px-3 py-2 text-right font-semibold">Cantidad comprada</th>
                        <th className="px-3 py-2 text-right font-semibold">Precio unit.</th>
                        <th className="px-3 py-2 text-right font-semibold">Costo stock</th>
                        <th className="px-3 py-2 text-right font-semibold">Pagos</th>
                        <th className="px-3 py-2 text-right font-semibold">Estado</th>
                        {canDeleteStock && (
                          <th className="px-3 py-2 text-right font-semibold">Acciones</th>
                        )}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {stockItems.map((stock) => {
                        const summary = getPaymentSummary(stock);
                        const status = stock.paymentStatus || summary.paymentStatus;
                        const paidAmountValue = stock.paidAmount ?? summary.paidAmount;
                        const balanceValue = stock.balance ?? summary.balance;
                        const stockAmounts = calculateStockAmounts(stock);
                        const standardGranel = isStandardGranelPurchase({
                          isImportList,
                          purchaseMode: stock.purchaseMode,
                      metricUnit: stock.metricUnit,
                        });
                        const purchaseDisplay = standardGranel
                          ? createStockEditForm(stock, { isImportList })
                          : null;
                        const unitPrice = Number(purchaseDisplay?.price ?? stock.price ?? 0);
                        const unitPriceWithoutIva = Number(
                          purchaseDisplay?.priceWithoutIva ?? stock.priceWithoutIva ?? 0
                        );
                        const qty = standardGranel
                          ? getStandardGranelDisplayWeight(stock, { isImportList })
                          : Number(stock.quantity || 0);
                        const subtotal = summary.stockCostAmount;
                        const estimateReview = estimateReviewByStockId.get(Number(stock.id));
                        const missingConversions = resolveMissingImportConversions(stock);

                        return (
                          <tr
                            key={stock.id}
                            className="hover:bg-gray-50 cursor-pointer"
                            onClick={() => setSelectedItem(stock)}
                          >
                            <td className="px-3 py-3 text-left">
                              <div className="flex items-center gap-3">
                                <img
                                  src={stock.varietyImage || "https://placehold.co/200"}
                                  alt={stock.varietyName || "Producto sin nombre"}
                                  className="w-12 h-12 object-cover rounded"
                                />
                                <div className="flex flex-col min-w-0">
                                  <span className="font-semibold text-gray-900 break-words">
                                    {stock.varietyName || "Producto"}
                                  </span>
                                  <StockFormatRegistrationNotice stock={stock} listStatus={stockList?.status} onRegister={openStockRegistration} canManageFormats={canManageFormats} />
                                  {stock.Provider?.name && (
                                    <span className="text-xs text-gray-500 break-words">
                                      Proveedor: {stock.Provider.name}
                                    </span>
                                  )}
                                  {estimateReview?.needsReview && (
                                    <span className="mt-1 inline-flex w-fit px-2 py-0.5 text-[10px] font-semibold rounded-full bg-amber-100 text-amber-700">
                                      Revisar estimado
                                    </span>
                                  )}
                                  {isImportList && missingConversions.length > 0 && (
                                    <span className="mt-1 inline-flex w-fit px-2 py-0.5 text-[10px] font-semibold rounded-full bg-amber-100 text-amber-700">
                                      Conversion pendiente
                                    </span>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="px-3 py-3 text-left text-gray-600">
                              <p><span className="font-semibold">Compra:</span> {stockPackLabel(stock) || "unidad"}</p>
                              <div className="mt-1">
                                <StockSellingEstimateSummary meta={estimateMetaByStockId.get(Number(stock.id))} loading={loadingWholesale} />
                              </div>
                              {isImportList && (
                                <span className="block text-xs text-indigo-700">
                                  {stock.lengthCm && stock.widthCm && stock.heightCm
                                    ? `${formatSignificantNumber(stock.lengthCm)} x ${formatSignificantNumber(stock.widthCm)} x ${formatSignificantNumber(stock.heightCm)} cm`
                                    : "Dimensiones pendientes"}
                                </span>
                              )}
                            </td>
                            <td className="px-3 py-3 text-right text-gray-700">
                              {formatSignificantNumber(qty)}{standardGranel ? " kg" : ""}
                              {standardGranel && (
                                <span className="block text-xs text-sky-700">Peso total</span>
                              )}
                              {isImportList && (
                                <span className="block text-xs text-indigo-700">
                                  {formatKg(Number(stock.weightKg ?? stock.weight ?? 0))} kg/u
                                </span>
                              )}
                            </td>
                            <td className="px-3 py-3 text-right text-gray-700">
                              ${formatPesoAmount(unitPrice)}
                              {standardGranel && (
                                <span className="block text-xs text-sky-700">Monto total</span>
                              )}
                              {unitPriceWithoutIva > 0 && (
                                <span className="block text-xs text-gray-500">
                                  Monto comprado sin IVA: ${formatPesoAmount(unitPriceWithoutIva)}
                                </span>
                              )}
                            </td>
                            <td className="px-3 py-3 text-right font-semibold text-gray-900">
                              ${formatPesoAmount(subtotal)}
                              {stockAmounts.ivaCostUpliftAmount > 0 && (
                                <span className="block text-xs font-normal text-gray-500">
                                  IVA no adeudado: ${formatPesoAmount(stockAmounts.ivaCostUpliftAmount)}
                                </span>
                              )}
                            </td>
                            <td className="px-3 py-3 text-right text-xs text-gray-600">
                              <div className="flex flex-col items-end">
                                <span>Pagado: ${formatPesoAmount(Number(paidAmountValue || 0))}</span>
                                <span>Pendiente: ${formatPesoAmount(Number(balanceValue || 0))}</span>
                              </div>
                            </td>
                            <td className="px-3 py-3 text-right">
                              {status && (
                                <span
                                  className={`inline-flex px-2 py-1 text-xs font-semibold rounded ${paymentStatusClass[status] || "bg-gray-100 text-gray-600"}`}
                                >
                                  {paymentStatusLabel[status] || "Pendiente"}
                                </span>
                              )}
                            </td>
                            {canDeleteStock && (
                              <td className="px-3 py-3 text-right">
                                <button
                                  type="button"
                                  onClick={(event) => handleDeleteStockFromList(event, stock)}
                                  disabled={deletingStockId === stock.id}
                                  className="bg-red-500 text-white p-2 rounded hover:bg-red-600 disabled:opacity-60"
                                  aria-label="Borrar compra"
                                  title="Borrar compra"
                                >
                                  {deletingStockId === stock.id ? (
                                    <span className="text-xs">...</span>
                                  ) : (
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      fill="none"
                                      viewBox="0 0 24 24"
                                      strokeWidth="1.5"
                                      stroke="currentColor"
                                      className="w-4 h-4"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.166L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.021-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
                                      />
                                    </svg>
                                  )}
                                </button>
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}

        {/* Expenses Tab */}
        {activeTab === "gastos" && (
          <div className="grid grid-cols-1 gap-4">
            {expenses.length === 0 ? (
              <p className="text-center text-gray-500">No hay gastos registrados.</p>
            ) : (
              expenses.map((expense) => (
                <article
                  key={expense.id}
                  aria-label={`Gasto: ${expense.title}`}
                  className="p-5 bg-white rounded-lg shadow"
                >
                  <h3 className="text-lg font-semibold"><button type="button" className="text-left hover:underline" aria-label={`Ver gasto ${expense.title}`} onClick={() => setExpenseTarget({ expense, mode: 'view', scopeKey })}>{expense.title}</button></h3>
                  <p className="text-gray-600">{expense.description}</p>
                  <p className="text-gray-800 font-bold">Monto: ${formatPesoAmount(Number(expense.amount || 0))}</p>
                  {isImportList && (
                    <p className="text-sm text-indigo-700 font-semibold">
                      Prorrateo: {{
                        volume: "Volumen",
                        weight: "Peso",
                        price: "Precio",
                      }[expense.allocationBasis] || "Pendiente"}
                    </p>
                  )}
                  <div className="mt-3 flex flex-wrap gap-2">
                    {canUpdateStock && <button type="button" className="rounded-lg border border-blue-200 px-3 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-50" aria-label={`Editar gasto ${expense.title}`} onClick={() => setExpenseTarget({ expense, mode: 'edit', scopeKey })}>Editar gasto</button>}
                    {canDeleteStock && <button type="button" className="rounded-lg border border-red-200 px-3 py-2 text-sm font-semibold text-red-700 hover:bg-red-50" aria-label={`Eliminar gasto ${expense.title}`} onClick={() => setExpenseTarget({ expense, mode: 'delete', scopeKey })}>Eliminar gasto</button>}
                  </div>
                </article>
              ))
            )}
          </div>
        )}

        {/* Detail Modal */}
        {selectedItem && (
          <div
            className="fixed inset-0 bg-slate-900/60 flex justify-center items-start z-50 px-4 py-6 overflow-y-auto"
            onClick={(event) => event.stopPropagation()}
          >
            <div
              className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl border border-slate-200 overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between gap-4 bg-slate-50 border-b border-slate-200 px-6 py-4">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    {activeTab === "productos" ? "Detalle de compra" : "Detalle de gasto"}
                  </p>
                  <h2 className="text-xl font-semibold text-slate-900">
                    {activeTab === "productos"
                      ? selectedItem.varietyName || "Producto"
                      : selectedItem.title}
                  </h2>
                </div>
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="text-slate-500 hover:text-slate-900"
                  aria-label="Cerrar"
                >
                  ✕
                </button>
              </div>

              <div className="px-6 py-5 space-y-5 max-h-[82vh] overflow-y-auto">
                {activeTab === "productos" ? (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-[220px_minmax(0,1fr)] gap-4">
                      <img
                        src={selectedItem.varietyImage || "https://placehold.co/320x240"}
                        alt={selectedItem.varietyName || "Producto"}
                        className="w-full h-48 md:h-full object-cover rounded-xl border border-slate-200"
                      />
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2">
                          <p className="text-xs text-gray-500">Proveedor</p>
                          {isEditingStock ? (
                            <div className="mt-1 relative" ref={editProviderSelectRef}>
                              <button
                                type="button"
                                onClick={() => {
                                  setIsEditProviderSelectOpen((prev) => !prev);
                                  if (!providersLoaded) loadEditProviders();
                                }}
                                className="border border-gray-300 rounded-lg p-2 w-full text-left bg-white flex items-center justify-between text-sm"
                              >
                                <span className={selectedEditProvider ? "text-gray-900" : "text-gray-500"}>
                                  {selectedEditProvider?.name || selectedItem.Provider?.name || "Selecciona un proveedor"}
                                </span>
                                <span className="text-gray-500">▾</span>
                              </button>
                              {isEditProviderSelectOpen && (
                                <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded shadow-lg">
                                  <div className="p-2 border-b border-gray-100">
                                    <input
                                      type="text"
                                      placeholder="Buscar proveedor..."
                                      value={editProviderSearchTerm}
                                      onChange={(event) => setEditProviderSearchTerm(event.target.value)}
                                      className="border border-gray-300 rounded p-2 w-full text-sm"
                                    />
                                  </div>
                                  <ul className="max-h-60 overflow-y-auto" role="listbox">
                                    {providersLoading ? (
                                      <li className="px-3 py-2 text-sm text-gray-500">Cargando proveedores...</li>
                                    ) : providersError ? (
                                      <li className="px-3 py-2 text-sm text-red-600">{providersError}</li>
                                    ) : filteredEditProviders.length === 0 ? (
                                      <li className="px-3 py-2 text-sm text-gray-500">
                                        {editProviderSearchTerm ? "Sin resultados" : "No hay proveedores"}
                                      </li>
                                    ) : (
                                      filteredEditProviders.map((provider) => {
                                        const isSelected = String(provider.id) === selectedEditProviderId;
                                        return (
                                          <li key={provider.id}>
                                            <button
                                              type="button"
                                              onClick={() => handleSelectEditProvider(provider.id)}
                                              className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 ${
                                                isSelected ? "bg-gray-100 font-medium" : ""
                                              }`}
                                              role="option"
                                              aria-selected={isSelected}
                                            >
                                              <div className="font-medium text-gray-900">{provider.name}</div>
                                              {provider.rut && (
                                                <div className="text-xs text-gray-500">{provider.rut}</div>
                                              )}
                                            </button>
                                          </li>
                                        );
                                      })
                                    )}
                                  </ul>
                                </div>
                              )}
                            </div>
                          ) : (
                            <p className="text-sm font-semibold text-gray-900">{selectedItem.Provider?.name || "N/A"}</p>
                          )}
                        </div>
                        <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2">
                          <p className="text-xs text-gray-500">{isEditingStock ? "Estado proyectado" : "Estado"}</p>
                          <span
                            className={`inline-flex mt-1 px-2.5 py-1 rounded-full text-xs font-semibold ${
                              paymentStatusClass[selectedSummary?.paymentStatus || selectedItem.paymentStatus] || "bg-gray-100 text-gray-700"
                            }`}
                          >
                            {paymentStatusLabel[selectedSummary?.paymentStatus || selectedItem.paymentStatus] || "Pendiente"}
                          </span>
                        </div>
                        {!selectedStandardGranel && (
                          <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2">
                            <p className="text-xs text-gray-500">Cantidad</p>
                            {isEditingStock ? (
                              <input
                                type="number"
                                inputMode="decimal"
                                min="0"
                                step="0.001"
                                value={stockEditForm.quantity}
                                onChange={(event) => handleStockEditChange("quantity", event.target.value)}
                                className="mt-1 border border-gray-300 rounded-lg p-2 w-full text-sm bg-white"
                                aria-describedby={selectedPurchaseGuidance ? "standard-stock-edit-guidance" : undefined}
                              />
                            ) : (
                              <p className="text-sm font-semibold text-gray-900">{formatSignificantNumber(selectedItem.quantity)}</p>
                            )}
                          </div>
                        )}
                        <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2">
                          <p className="text-xs text-gray-500">Unidad</p>
                          {isEditingStock && selectedUsesFormatSelector ? (
                            <StockPurchaseFormatSelector branchId={branchId} formats={selectedPurchaseFormats} draft={stockEditForm}
                              idPrefix="stock-edit" disabled={isSavingStockEdit}
                              onChange={(next) => { setStockEditForm(next); setStockEditError(''); }} />
                          ) : isEditingStock ? (
                            <select
                              value={stockEditForm.metricUnit}
                              onChange={(event) => handleStockEditMetricUnitChange(event.target.value)}
                              className="mt-1 border border-gray-300 rounded-lg p-2 w-full text-sm bg-white"
                            >
                              <option value="">Selecciona una unidad</option>
                              {stockMetricUnitOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <p className="text-sm font-semibold text-gray-900">{stockPackLabel(selectedItem) || "N/A"}</p>
                          )}

                        </div>
                        <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2">
                          <p className="text-xs text-gray-500">Monto comprado con IVA</p>
                          {isEditingStock ? (
                            <input
                              type="number"
                              inputMode="numeric"
                              min="0"
                              step="1"
                              value={stockEditForm.price}
                              onChange={(event) => handleStockEditChange("price", event.target.value)}
                              className="mt-1 border border-gray-300 rounded-lg p-2 w-full text-sm bg-white"
                              aria-describedby={selectedPurchaseGuidance ? "standard-stock-edit-guidance" : undefined}
                            />
                          ) : (
                            <p className="text-sm font-semibold text-gray-900">
                              ${formatPesoAmount(Number(
                                selectedStandardGranel
                                  ? selectedPurchaseDisplayForm.price
                                  : selectedItem.price || 0
                              ))}
                            </p>
                          )}
                        </div>
                        <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2">
                          <p className="text-xs text-gray-500">Monto comprado sin IVA</p>
                          {isEditingStock ? (
                            <input
                              type="number"
                              inputMode="numeric"
                              min="0"
                              step="1"
                              value={stockEditForm.priceWithoutIva}
                              onChange={(event) => handleStockEditChange("priceWithoutIva", event.target.value)}
                              className="mt-1 border border-gray-300 rounded-lg p-2 w-full text-sm bg-white"
                              aria-describedby={selectedPurchaseGuidance ? "standard-stock-edit-guidance" : undefined}
                            />
                          ) : (
                            <p className="text-sm font-semibold text-gray-900">
                              ${formatPesoAmount(Number(
                                selectedStandardGranel
                                  ? selectedPurchaseDisplayForm.priceWithoutIva
                                  : selectedItem.priceWithoutIva || 0
                              ))}
                            </p>
                          )}
                        </div>
                        <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2">
                          <p className="text-xs text-gray-500">Adeudado proveedor</p>
                          <p className="text-sm font-semibold text-gray-900">
                            ${formatPesoAmount(Number(selectedSummary?.totalAmount || 0))}
                          </p>
                        </div>
                        <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2">
                          <p className="text-xs text-gray-500">Costo stock</p>
                          <p className="text-sm font-semibold text-gray-900">
                            ${formatPesoAmount(Number(selectedSummary?.stockCostAmount || 0))}
                          </p>
                          {Number(selectedSummary?.ivaCostUpliftAmount || 0) > 0 && (
                            <p className="text-xs text-gray-500">
                              IVA no adeudado: ${formatPesoAmount(Number(selectedSummary?.ivaCostUpliftAmount || 0))}
                            </p>
                          )}
                        </div>
                        <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2">
                          <p className="text-xs text-gray-500">Pagado</p>
                          <p className="text-sm font-semibold text-gray-900">
                            ${formatPesoAmount(Number(selectedSummary?.paidAmount || 0))}
                          </p>
                        </div>
                        <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2">
                          <p className="text-xs text-gray-500">Pendiente</p>
                          <p className="text-sm font-semibold text-gray-900">
                            ${formatPesoAmount(Number(selectedSummary?.balance || 0))}
                          </p>
                        </div>
                        <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2">
                          <p className="text-xs text-gray-500">Método de pago</p>
                          {isEditingStock ? (
                            <select
                              value={stockEditForm.paymentMethod}
                              onChange={(event) => handleStockEditChange("paymentMethod", event.target.value)}
                              className="mt-1 border border-gray-300 rounded-lg p-2 w-full text-sm bg-white"
                            >
                              <option value="">Selecciona un método</option>
                              {paymentMethods.map((method) => (
                                <option key={method.id} value={method.id}>
                                  {method.label}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <p className="text-sm font-semibold text-gray-900">
                              {paymentMethods.find((method) => method.id === selectedItem.paymentMethod)?.label || selectedItem.paymentMethod || "N/A"}
                            </p>
                          )}
                        </div>
                        {(!isEditingStock || !selectedUsesFormatSelector) && <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2">
                          <p className="text-xs text-gray-500">
                            {isImportList
                              ? `Peso por unidad${selectedIsResaleOnlyImport ? " (opcional)" : ""}`
                              : selectedPurchaseGuidance?.weightLabel || "Peso"}
                          </p>
                          {isEditingStock ? (
                            <input
                              type="number"
                              inputMode={selectedPurchaseGuidance && isBinUnit(stockEditForm.metricUnit) ? "numeric" : "decimal"}
                              min={selectedPurchaseGuidance ? (isBinUnit(stockEditForm.metricUnit) ? "1" : "0.001") : "0"}
                              step={selectedPurchaseGuidance && isBinUnit(stockEditForm.metricUnit) ? "1" : "0.001"}
                              value={stockEditForm.weight}
                              onChange={(event) => handleStockEditChange("weight", event.target.value)}
                              className="mt-1 border border-gray-300 rounded-lg p-2 w-full text-sm bg-white"
                              placeholder="Kg"
                              aria-describedby={selectedPurchaseGuidance ? "standard-stock-edit-guidance" : undefined}
                            />
                          ) : (
                            <p className="text-sm font-semibold text-gray-900">
                              {selectedStandardGranel
                                ? getStandardGranelDisplayWeight(selectedItem, { isImportList }) || "N/A"
                                : selectedItem.purchaseSnapshot?.weightKg ?? selectedItem.weightKg ?? selectedItem.weight ?? "N/A"} kg
                            </p>
                          )}
                        </div>}
                        {selectedPurchaseGuidance && (
                          <div
                            id="standard-stock-edit-guidance"
                            className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs leading-relaxed text-black sm:col-span-2"
                          >
                            <p>{selectedPurchaseGuidance.amount}</p>
                            <p className="mt-1">{selectedPurchaseGuidance.weight}</p>
                          </div>
                        )}
                        {isImportList && (
                          <div className="rounded-xl border border-indigo-200 bg-indigo-50 px-3 py-2 sm:col-span-2">
                            <p className="text-xs text-indigo-700">
                              Dimensiones{selectedIsResaleOnlyImport ? " (opcional)" : ""}
                            </p>
                            {isEditingStock ? (
                              <div className="mt-1 grid grid-cols-1 sm:grid-cols-3 gap-2">
                                <input
                                  type="number"
                                  inputMode="decimal"
                                  min="0"
                                  step="0.001"
                                  value={stockEditForm.lengthCm}
                                  onChange={(event) => handleStockEditChange("lengthCm", event.target.value)}
                                  className="border border-indigo-200 rounded-lg p-2 w-full text-sm bg-white"
                                  placeholder="Largo cm"
                                />
                                <input
                                  type="number"
                                  inputMode="decimal"
                                  min="0"
                                  step="0.001"
                                  value={stockEditForm.widthCm}
                                  onChange={(event) => handleStockEditChange("widthCm", event.target.value)}
                                  className="border border-indigo-200 rounded-lg p-2 w-full text-sm bg-white"
                                  placeholder="Ancho cm"
                                />
                                <input
                                  type="number"
                                  inputMode="decimal"
                                  min="0"
                                  step="0.001"
                                  value={stockEditForm.heightCm}
                                  onChange={(event) => handleStockEditChange("heightCm", event.target.value)}
                                  className="border border-indigo-200 rounded-lg p-2 w-full text-sm bg-white"
                                  placeholder="Alto cm"
                                />
                              </div>
                            ) : (
                              <p className="text-sm font-semibold text-indigo-950">
                                {selectedItem.lengthCm && selectedItem.widthCm && selectedItem.heightCm
                                  ? `${formatSignificantNumber(selectedItem.lengthCm)} x ${formatSignificantNumber(selectedItem.widthCm)} x ${formatSignificantNumber(selectedItem.heightCm)} cm`
                                  : "Pendiente"}
                              </p>
                            )}
                          </div>
                        )}
                        {!selectedIsFormatPurchase && isStockPack(selectedDisplayMetricUnit) && (
                          <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 sm:col-span-2">
                            <label htmlFor="stock-edit-pack-size" className="text-xs text-gray-500">Unidades por pack</label>
                            {isEditingStock ? <input id="stock-edit-pack-size" type="number" min="1" step="1" inputMode="numeric"
                              value={stockEditForm.unitsPerPack} onChange={(event) => handleStockEditChange("unitsPerPack", event.target.value)}
                              className="mt-1 border border-gray-300 rounded-lg p-2 w-full text-sm bg-white" />
                              : <p className="text-sm font-semibold text-gray-900">{selectedItem.unitsPerPack}</p>}
                            <p className="mt-1 text-xs text-gray-600" aria-live="polite">{stockPackTotal(isEditingStock ? stockEditForm : selectedItem)}</p>
                          </div>
                        )}
                        {!selectedIsFormatPurchase && <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 sm:col-span-2">
                          <p className="text-xs text-gray-500">Bultos por bin</p>
                          {isEditingStock ? (
                            !selectedIsFormatPurchase && isBinUnit(stockEditForm.metricUnit) ? (
                              <input
                                type="number"
                                inputMode="numeric"
                                min="1"
                                step="1"
                                value={stockEditForm.bultosPerBin}
                                onChange={(event) => handleStockEditChange("bultosPerBin", event.target.value)}
                                className="mt-1 border border-gray-300 rounded-lg p-2 w-full text-sm bg-white"
                              />
                            ) : (
                              <p className="text-sm text-gray-500 mt-1">
                                Solo aplica cuando la unidad es Bin.
                              </p>
                            )
                          ) : (
                            <p className="text-sm font-semibold text-gray-900">
                              {isBinUnit(selectedItem.metricUnit) ? (selectedItem.bultosPerBin || "N/A") : "No aplica"}
                            </p>
                          )}
                        </div>}
                      </div>
                    </div>
                    {isEditingStock && stockEditError && (
                      <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                        {stockEditError}
                      </div>
                    )}

                    {isImportList && selectedMissingImportConversions.length > 0 && (
                      <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                        <p className="font-semibold">Conversiones pendientes</p>
                        <p>
                          Faltan conversiones desde {selectedItem.metricUnit || "unidad de compra"} a{" "}
                          {selectedMissingImportConversions.map((unit) => unit.label).join(", ")}.
                        </p>
                      </div>
                    )}

                    <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3">
                      <h3 className="text-sm font-semibold text-emerald-900">Aporte al estimado mayorista general</h3>
                      {selectedEstimateMeta?.priceSource === 'draft' && (
                        <p className="mt-1 text-xs text-emerald-800">Precio provisional según el costo de esta compra y su regla de precio.</p>
                      )}
                      <p className="mt-2 text-sm text-emerald-900">
                        <span className="font-semibold">Formato de venta:</span>{" "}
                        {loadingWholesale ? "Cargando..." : sellingFormatLabel(selectedEstimateMeta)}
                      </p>
                      {selectedEstimateMeta?.considered ? (
                        <div className="mt-2 space-y-1 text-sm text-emerald-900">
                          <p>
                            <span className="font-semibold">Cantidad comprada:</span>{" "}
                            {formatSignificantNumber(selectedEstimateMeta.purchaseQuantity)} {selectedEstimateMeta.purchaseMetricUnit}
                          </p>
                          {selectedEstimateMeta.mode === "weight" && (
                            <p>
                              <span className="font-semibold">Conversión a venta:</span>{" "}
                              {formatSignificantNumber(selectedEstimateMeta.totalWeightKg)} kg ÷ {formatSignificantNumber(selectedEstimateMeta.sellingWeightKg)} kg por {selectedEstimateMeta.wholesaleMetricUnit} = {formatSignificantNumber(selectedEstimateMeta.quantity)} {selectedEstimateMeta.wholesaleMetricUnit}
                            </p>
                          )}
                          <p>
                            <span className="font-semibold">Precio por formato de venta:</span>{" "}
                            ${formatPesoAmount(Number(selectedEstimateMeta.unitPrice || 0))}
                            {selectedEstimateMeta.metricUnit ? ` / ${selectedEstimateMeta.metricUnit}` : ""}
                          </p>
                          {selectedEstimateMeta.usedEquivalence && selectedEstimateMeta?.equivalence && (
                            <p>
                              <span className="font-semibold">Equivalencia:</span>{" "}
                              {formatSignificantNumber(selectedEstimateMeta.equivalence.sourceAmount || 0)} {selectedEstimateMeta.equivalence.sourceUnit} = {formatSignificantNumber(selectedEstimateMeta.equivalence.targetAmount || 0)} {selectedEstimateMeta.equivalence.targetUnit}
                            </p>
                          )}
                          <p>
                            <span className="font-semibold">Origen:</span>{" "}
                            {selectedEstimateSourceLabel}
                            {selectedEstimateMeta.wholesaleMetricUnit ? ` (${selectedEstimateMeta.wholesaleMetricUnit})` : ""}
                          </p>
                          <p>
                            <span className="font-semibold">Cálculo:</span>{" "}
                            {formatSignificantNumber(selectedEstimateMeta.quantity)} {selectedEstimateMeta.wholesaleMetricUnit} × ${formatPesoAmount(Number(selectedEstimateMeta.unitPrice))}
                          </p>
                          <p>
                            <span className="font-semibold">Estimado item:</span>{" "}
                            ${formatPesoAmount(selectedEstimateValue)}
                          </p>
                          <p>
                            <span className="font-semibold">Participación en total estimado:</span>{" "}
                            {selectedEstimateShare.toLocaleString("de-DE", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                            %
                          </p>
                          <p>
                            <span className="font-semibold">Total estimado general:</span>{" "}
                            ${formatPesoAmount(wholesaleEstimate)}
                          </p>
                          {selectedNeedsEstimateReview ? (
                            <p className="text-amber-800 font-semibold">
                              Este item supera {ESTIMATE_WARNING_THRESHOLD}% del estimado. Confírmalo o crea equivalencia.
                            </p>
                          ) : selectedEstimateConfirmed ? (
                            <p className="text-emerald-800 font-semibold">
                              Item confirmado para esta lista.
                            </p>
                          ) : null}
                        </div>
                      ) : (
                        <div className="mt-2 text-sm text-emerald-900">
                          <p className="font-semibold">No considerado en el estimado general.</p>
                          <p className="text-emerald-800">
                            {selectedEstimateMeta?.reason || "Sin precio mayorista disponible para esta compra."}
                          </p>
                        </div>
                      )}
                      {canUpdateStock && !isEditingStock && selectedEstimateMeta?.considered && (selectedNeedsEstimateReview || selectedEstimateConfirmed || selectedEstimateMeta.usedEquivalence) && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {selectedEstimateConfirmed ? (
                            <button
                              type="button"
                              onClick={handleUnconfirmSelectedEstimate}
                              disabled={isSavingEstimateConfirmation}
                              className="px-3 py-2 rounded-lg text-xs font-semibold bg-gray-200 text-gray-800 hover:bg-gray-300 disabled:opacity-60"
                            >
                              {isSavingEstimateConfirmation ? "Guardando..." : "Quitar confirmación"}
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={handleConfirmSelectedEstimate}
                              disabled={isSavingEstimateConfirmation}
                              className="px-3 py-2 rounded-lg text-xs font-semibold bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60"
                            >
                              {isSavingEstimateConfirmation ? "Guardando..." : "Confirmar para esta lista"}
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={handleOpenEquivalenceModal}
                            disabled={isSavingEquivalence || selectedStockEquivalenceOptions.length === 0}
                            className="px-3 py-2 rounded-lg text-xs font-semibold bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
                          >
                            Crear equivalencia
                          </button>
                        </div>
                      )}
                    </div>

                    <div className="space-y-2">
                      <h3 className="text-sm font-semibold text-gray-900">Pagos registrados</h3>
                      {selectedItem.StockPayments && selectedItem.StockPayments.length > 0 ? (
                        <div className="space-y-2">
                          {selectedItem.StockPayments.map((payment) => (
                            <div
                              key={payment.id}
                              className="border border-slate-200 rounded-xl p-3 bg-white text-sm"
                            >
                              <p>
                                <span className="font-semibold">Monto:</span>{" "}
                                ${formatPesoAmount(Number(payment.amount || 0))}
                              </p>
                              <p>
                                <span className="font-semibold">Metodo:</span>{" "}
                                {payment.method}
                                {(payment.source === "general" || payment.providerPaymentId) && (
                                  <span className="ml-2 text-xs font-semibold px-2 py-1 rounded bg-blue-100 text-blue-700">
                                    Pago general
                                  </span>
                                )}
                              </p>
                              {payment.reference && (
                                <p>
                                  <span className="font-semibold">Referencia:</span>{" "}
                                  {payment.reference}
                                </p>
                              )}
                              {payment.voucherUrl && (
                                <p>
                                  <span className="font-semibold">Comprobante:</span>{" "}
                                  <a
                                    href={payment.voucherUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-blue-600 underline"
                                  >
                                    Ver
                                  </a>
                                </p>
                              )}
                              {payment.paidAt && (
                                <p>
                                  <span className="font-semibold">Fecha:</span>{" "}
                                  {new Date(payment.paidAt).toLocaleDateString("es-CL")}
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-sm text-gray-600 space-y-1 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2">
                          <p>Sin pagos registrados.</p>
                          {selectedItem.paymentMethod && (
                            <p>Metodo registrado: {selectedItem.paymentMethod}</p>
                          )}
                        </div>
                      )}
                    </div>

                    {canUpdateStock && Number(selectedSummary?.balance || 0) > 0 && (
                      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-3">
                        <h3 className="text-sm font-semibold text-gray-900">Agregar pago</h3>
                        {paymentError && (
                          <p className="text-sm text-red-600">{paymentError}</p>
                        )}
                        {isEditingStock && (
                          <p className="text-xs text-amber-700">
                            Termina la edición de la compra para registrar nuevos pagos.
                          </p>
                        )}
                        <div className="text-xs text-gray-600">
                          Pendiente actual: ${formatPesoAmount(selectedBalance)} | Restante despues de este pago: ${formatPesoAmount(remainingAfter)}
                        </div>
                        {overpayAmount > 0 && (
                          <p className="text-xs text-red-600">
                            Estas pagando de mas por ${formatPesoAmount(overpayAmount)}.
                          </p>
                        )}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          <input
                            type="number"
                            placeholder="Monto"
                            value={paymentForm.amount}
                            onChange={(e) => handlePaymentChange("amount", e.target.value)}
                            className="border border-gray-300 rounded-lg p-2 w-full"
                            disabled={isEditingStock || isSavingPayment}
                          />
                          <select
                            value={paymentForm.method}
                            onChange={(e) => handlePaymentChange("method", e.target.value)}
                            className="border border-gray-300 rounded-lg p-2 w-full"
                            disabled={isEditingStock || isSavingPayment}
                          >
                            <option value="">Metodo</option>
                            {paymentMethods.map((method) => (
                              <option key={method.id} value={method.id}>
                                {method.label}
                              </option>
                            ))}
                          </select>
                          <input
                            type="text"
                            placeholder="Referencia"
                            value={paymentForm.reference}
                            onChange={(e) => handlePaymentChange("reference", e.target.value)}
                            className="border border-gray-300 rounded-lg p-2 w-full"
                            disabled={isEditingStock || isSavingPayment}
                          />
                          <input
                            type="date"
                            value={paymentForm.paidAt}
                            onChange={(e) => handlePaymentChange("paidAt", e.target.value)}
                            className="border border-gray-300 rounded-lg p-2 w-full"
                            disabled={isEditingStock || isSavingPayment}
                          />
                          <div className="md:col-span-2">
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              Comprobante (opcional)
                            </label>
                            <input
                              type="file"
                              accept="image/*,application/pdf"
                              onChange={(e) =>
                                handlePaymentVoucherChange(e.target.files?.[0] || null)
                              }
                              className="border border-gray-300 rounded-lg p-2 w-full"
                              disabled={isEditingStock || isSavingPayment}
                            />
                            {paymentForm.voucherFile?.name && (
                              <p className="text-xs text-gray-500 mt-1">
                                {paymentForm.voucherFile.name}
                              </p>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={handleAddPayment}
                          disabled={isSavingPayment || isEditingStock}
                          className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-60"
                        >
                          {isSavingPayment ? <InlineLoading label="Guardando" /> : "Registrar pago"}
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 space-y-2 text-sm text-gray-700">
                    <p><span className="font-semibold">Descripción:</span> {selectedItem.description}</p>
                    <p><span className="font-semibold">Monto:</span> ${formatPesoAmount(selectedItem.amount)}</p>
                    {isImportList && (
                      <p>
                        <span className="font-semibold">Prorrateo:</span>{" "}
                        {{
                          volume: "Volumen",
                          weight: "Peso",
                          price: "Precio",
                        }[selectedItem.allocationBasis] || "Pendiente"}
                      </p>
                    )}
                  </div>
                )}
              </div>

              <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex flex-col sm:flex-row gap-2 sm:justify-end">
                {activeTab === "productos" && isEditingStock && canUpdateStock && (
                  <>
                    <button
                      onClick={handleCancelEditingStock}
                      disabled={isSavingStockEdit}
                      className="w-full sm:w-auto px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 disabled:opacity-60"
                    >
                      Cancelar edición
                    </button>
                    <button
                      onClick={handleSaveStockEdit}
                      disabled={isSavingStockEdit}
                      className="w-full sm:w-auto px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-60"
                    >
                      {isSavingStockEdit ? "Guardando..." : "Guardar cambios"}
                    </button>
                  </>
                )}
                {activeTab === "productos" && !isEditingStock && canUpdateStock && (
                  <button
                    onClick={handleStartEditingStock}
                    className="w-full sm:w-auto px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Editar compra
                  </button>
                )}
                {activeTab === "productos" && !isEditingStock && canDeleteStock && (
                  <button
                    onClick={handleDeleteStockItem}
                    disabled={isDeletingStock}
                    className="w-full sm:w-auto px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-60 flex items-center justify-center"
                    aria-label="Borrar compra"
                    title="Borrar compra"
                  >
                    {isDeletingStock ? (
                      <span className="text-sm">...</span>
                    ) : (
                      "Eliminar compra"
                    )}
                  </button>
                )}
                <button
                  onClick={handleCloseModal}
                  className="w-full sm:w-auto px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        )}
        {equivalenceModal.isOpen && (
          <div
            className="fixed inset-0 bg-black/50 flex items-start justify-center z-[60] px-4 py-8 overflow-y-auto"
            onClick={(event) => event.stopPropagation()}
          >
            <div
              className="w-full max-w-lg bg-white rounded-2xl border border-slate-200 shadow-2xl overflow-hidden"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="px-5 py-4 border-b border-slate-200 bg-slate-50 flex items-center justify-between gap-3">
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-500">Estimado mayorista</p>
                  <h3 className="text-base font-semibold text-slate-900">Crear equivalencia global</h3>
                </div>
                <button
                  type="button"
                  onClick={handleCloseEquivalenceModal}
                  className="text-slate-500 hover:text-slate-900"
                  aria-label="Cerrar"
                >
                  ✕
                </button>
              </div>
              <div className="p-5 space-y-4">
                <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm">
                  <p className="text-xs text-gray-500">Unidad origen</p>
                  <p className="font-semibold text-gray-900">{equivalenceModal.fromUnit || "N/A"}</p>
                </div>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">x_amount</label>
                    <input
                      type="number"
                      inputMode="decimal"
                      min="0"
                      step="0.001"
                      placeholder="Ej: 1"
                      value={equivalenceModal.fromAmount}
                      onChange={(event) => setEquivalenceModal((prev) => ({
                        ...prev,
                        fromAmount: event.target.value,
                        error: "",
                      }))}
                      className="w-full border border-gray-300 rounded-lg p-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">from_metric</label>
                    <div className="w-full border border-gray-300 rounded-lg p-2 text-sm bg-gray-50 text-gray-800">
                      {equivalenceModal.fromUnit || "N/A"}
                    </div>
                  </div>
                  <p className="text-center text-sm font-semibold text-gray-600">=</p>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">y_amount</label>
                    <input
                      type="number"
                      inputMode="decimal"
                      min="0"
                      step="0.001"
                      placeholder="Ej: 18"
                      value={equivalenceModal.toAmount}
                      onChange={(event) => setEquivalenceModal((prev) => ({
                        ...prev,
                        toAmount: event.target.value,
                        error: "",
                      }))}
                      className="w-full border border-gray-300 rounded-lg p-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">to_metric</label>
                    <select
                      value={equivalenceModal.toUnit}
                      onChange={(event) => setEquivalenceModal((prev) => ({
                        ...prev,
                        toUnit: event.target.value,
                        error: "",
                      }))}
                      className="w-full border border-gray-300 rounded-lg p-2 text-sm"
                    >
                      {selectedStockEquivalenceOptions.map((option) => (
                        <option key={option.key} value={option.label}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <p className="text-[11px] text-gray-500 -mt-1">
                  {modalSourceAmountLabel} {equivalenceModal.fromUnit || "origen"} = {modalTargetAmountLabel} {equivalenceModal.toUnit || "destino"}
                </p>
                <div className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-900 space-y-1">
                  <p className="font-semibold">Como se usa esta equivalencia</p>
                  <p>
                    Se guarda para ambos sentidos.
                  </p>
                  <p>
                    1 {equivalenceModal.fromUnit || "origen"} = {modalForwardRatioLabel} {equivalenceModal.toUnit || "destino"}
                  </p>
                  <p>
                    1 {equivalenceModal.toUnit || "destino"} = {modalReverseRatioLabel} {equivalenceModal.fromUnit || "origen"}
                  </p>
                  <p className="text-[11px] text-blue-800">
                    Formula aplicada al estimado: precio por {equivalenceModal.fromUnit || "origen"} = precio por {equivalenceModal.toUnit || "destino"} x ({modalTargetAmountLabel} / {modalSourceAmountLabel})
                  </p>
                </div>
                {equivalenceModal.error && (
                  <p className="text-xs text-red-600">{equivalenceModal.error}</p>
                )}
                {selectedEquivalencesForSource.length > 0 && (
                  <div className="rounded-lg border border-gray-200 p-3 space-y-2">
                    <p className="text-xs font-semibold text-gray-600 uppercase">Equivalencias actuales para esta unidad</p>
                    {selectedEquivalencesForSource.map((entry) => (
                      <div key={entry.id} className="flex items-center justify-between gap-2 text-sm">
                        <span className="text-gray-800">
                          {formatSignificantNumber(entry.sourceAmount || 0)} {entry.sourceUnit} = {formatSignificantNumber(entry.targetAmount || 0)} {entry.targetUnit}
                        </span>
                        {canUpdateStock && (
                          <button
                            type="button"
                            onClick={() => handleDeleteEquivalence(entry.id)}
                            disabled={deletingEquivalenceId === entry.id}
                            className="px-2 py-1 text-xs rounded bg-red-100 text-red-700 hover:bg-red-200 disabled:opacity-60"
                          >
                            {deletingEquivalenceId === entry.id ? "..." : "Eliminar"}
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="px-5 py-4 border-t border-slate-200 bg-slate-50 flex flex-col sm:flex-row gap-2 sm:justify-end">
                <button
                  type="button"
                  onClick={handleCloseEquivalenceModal}
                  className="w-full sm:w-auto px-4 py-2 rounded-lg bg-gray-200 text-gray-800 hover:bg-gray-300"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleSaveEquivalence}
                  disabled={isSavingEquivalence}
                  className="w-full sm:w-auto px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
                >
                  {isSavingEquivalence ? "Guardando..." : "Guardar equivalencia"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StockListViewPage;

export {
  getRecommendationCalculationDetails,
  getRecommendationCalculationExpression,
  getRecommendationRoundingMeta,
  parseRecommendationRuleFromLabel,
};
